import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "./AuthContext";
import LoginScreen from "./components/LoginScreen";
import StorageSetup from "./components/StorageSetup";
import PinLock, { isPinEnabled, shouldAutoLock, updateLastActive, getAutoLockMins, setAutoLockMins, verifyPin } from "./components/PinLock";
import TradeJournal from "./components/TradeJournal";
import Reports from "./components/Reports";
import { getDarkMode, setDarkMode, theme } from "./components/DarkMode";
import {
  getStorageMode, setStorageMode,
  loadAccounts, saveAccount, setPrimaryAccount, removeAccount,
  loadTransactions, saveTxn, removeTxn,
  saveCurrentValue, saveActivityLog, saveGlobalActivityLog, loadActivityLogsForExport, getLocalVaultSizeBytes, getLocalVaultStatus, saveFeedback
} from "./storage";

import { APP_NAME, APP_VERSION, PROFILE_TYPES, CURRENCIES, PAYMENT_METHODS, STD_CATS, FEEDBACK_TYPES, isMetalProfile, getProfileMetal, getMetalProfileLabel, getProfileTypeMeta } from "./constants";
import { fmt, fmtFull, fmtDate, fmtGram, today, uid2, getSettings, saveSettings } from "./utils/formatters";
import { currentMonthKey, buildBudgetStats, getBudgetForMonth, toMoneyNumber } from "./utils/budget";
import { exportToPDF, exportToExcel } from "./utils/exporters";
import { downloadEncryptedBackup, isBackupEncryptionSupported } from "./utils/encryptedBackup";
import { createActivityLog, activityLogExportRows } from "./utils/activityLog";
import { clearLocalVaultSession } from "./utils/localVaultSecurity";
import { RECURRING_FREQUENCIES, normalizeRecurringRule, ruleToTransaction, getDueRecurringRules, addPeriod } from "./utils/productivity";
import { sanitizeAmount, sanitizeGram, sanitizePercentage, sanitizeTextInput, sanitizeNoteInput, sanitizeFeedbackMessageInput, sanitizeFeedbackMessage, sanitizeFeedbackType, FIELD_LIMITS, sanitizeAccountForStorage, sanitizeTransactionForStorage, sanitizeBackupForExport } from "./utils/security";
import { cleanMoneyInput, blockInvalidNumberKeys } from "./utils/numberInput";
import { openInternalPage, openExternalPage } from "./utils/navigation";
import { logWarn, logError } from "./utils/logger";
import {
  ProfileAvatar, AccFormSheet, StdForm, LendForm, TradeForm, GoldSilverForm, FDRDForm, TxnRow,
  FilterBar, TransactionFilters, TxnDesktopTable, QuickActionPanel, MoneyInsight, BudgetSnapshot, BudgetSummaryCard, BudgetSheet,
  CatBreakdown, PersonBreakdown, MiniCard, HeroCard, SR, Sheet,
  ConfirmDlg, EmptyState, ScrollPage, Wrap, GS, DesktopSidebar, FloatingAddButton, LoadingVault, BrandMark, LineIcon, navIconName, FreePlanSheet, SecuritySafetySheet
} from "./components/AppComponents";


const DEFAULT_HISTORY_FILTERS = {
  search: "",
  dateFrom: "",
  dateTo: "",
  minAmount: "",
  maxAmount: "",
  sort: "newest"
};

const SENSITIVE_SECRET_PATTERNS = [
  { label: "OTP / verification code", re: /\b(otp|one[-\s]?time password|verification code|login code|security code)\b/i },
  { label: "UPI / ATM / card PIN", re: /\b(upi\s*pin|atm\s*pin|card\s*pin|bank\s*pin|pin number)\b/i },
  { label: "card CVV/CVC", re: /\b(cvv|cvc|card verification value)\b/i },
  { label: "bank/login password", re: /\b(bank password|netbanking password|net banking password|internet banking password|login password)\b/i },
  { label: "full card number", re: /\b(card number|debit card number|credit card number)\b/i }
];

function detectSensitiveSecrets(text = "") {
  const value = String(text || "").trim();
  if (!value) return [];
  return SENSITIVE_SECRET_PATTERNS.filter(item => item.re.test(value)).map(item => item.label);
}

function sensitiveNoteMessage(text = "") {
  const found = detectSensitiveSecrets(text);
  if (!found.length) return "";
  return `Safety warning: notes appear to contain ${found.join(", ")}. FinVault never needs OTP, UPI PIN, CVV, card PIN, ATM PIN, or bank passwords. Remove these details before saving or sharing.`;
}


function lendingAutoStatus(opening = 0, closing = 0, settledLabel = "Settled") {
  const start = Number(opening || 0);
  const paid = Number(closing || 0);
  if (start <= 0) return "No activity";
  if (paid <= 0) return "Pending";
  if (paid < start) return "Partial";
  return settledLabel;
}

function buildLendingSummary(txns = []) {
  const sumType = (type) => txns.filter(t => t.txType === type).reduce((s, t) => s + Number(t.amount || 0), 0);
  const lent = sumType("lent");
  const returned = sumType("returned");
  const borrowed = sumType("borrowed");
  const repaid = sumType("repaid");
  const cashback = txns.reduce((s,t)=>s+Number(t.cashback||0)+(t.txType==="profit"?Number(t.amount||0):0),0);
  const outstandingReceivable = Math.max(0, lent - returned);
  const outstandingPayable = Math.max(0, borrowed - repaid);
  const netPosition = outstandingReceivable - outstandingPayable;
  return {
    lent, returned, borrowed, repaid, cashback,
    outstanding: outstandingReceivable,
    outstandingReceivable,
    owes: outstandingPayable,
    outstandingPayable,
    netPosition,
    receivableStatus: lendingAutoStatus(lent, returned, "Returned"),
    payableStatus: lendingAutoStatus(borrowed, repaid, "Repaid"),
  };
}

async function verifySensitiveAction(actionLabel = "continue") {
  // Phase 50: sensitive exports/deletes ask for the local FinVault PIN when enabled.
  // This protects finance data if the unlocked app is briefly accessed by someone else.
  if (!isPinEnabled()) return true;
  const pin = window.prompt(`Enter your FinVault PIN to ${actionLabel}.`);
  if (!pin) return false;
  const result = await verifyPin(pin);
  if (result?.ok) {
    updateLastActive();
    return true;
  }
  const message = result?.locked
    ? `Too many wrong PIN attempts. Try again in ${Math.ceil((result.remaining || 60) / 60)} min.`
    : result?.error || "Wrong PIN. Sensitive action cancelled.";
  alert(message);
  return false;
}

// ─── ROOT APP ────────────────────────────────────────────────
export default function App() {
  const { user, logout } = useAuth();
  const [storageMode, setMode]   = useState(() => getStorageMode());
  const [pinUnlocked, setPinUnlocked] = useState(() => !isPinEnabled());

  const [dark, setDark]          = useState(() => getDarkMode());

  const handleStorageDone = (mode) => {
    setStorageMode(mode);
    setMode(mode);
  };

  // Storage mode changes must reset the auth bootstrap path.
  // Without this, a previous Local Mode session can remain in memory and
  // users may see the local empty-profile screen when trying to move to Cloud.
  const handleChangeStorage = () => {
    localStorage.removeItem("finvault_storage_mode");
    window.location.reload();
  };
  const toggleDark = () => { setDark(d => { setDarkMode(!d); return !d; }); };

  // Step 1: Storage selection
  if (!storageMode) return <StorageSetup onDone={handleStorageDone} dark={dark} />;

  // Step 2: PIN lock
  if (!pinUnlocked) return <PinLock mode="verify" onSuccess={() => { updateLastActive(); setPinUnlocked(true); }} />;

  // Step 3: Login for cloud modes
  if (storageMode !== "local" && !user) {
    return <LoginScreen onChangeStorage={handleChangeStorage} dark={dark}/>;
  }

  // Step 4: Main app
  const effectiveUser = storageMode === "local"
    ? (user || { uid:"local_user", displayName:"Local User", email:"", photoURL:null, isLocal:true })
    : user;

  return <MainApp user={effectiveUser} logout={logout} storageMode={storageMode}
    onChangeStorage={handleChangeStorage} dark={dark} toggleDark={toggleDark}
    onLock={() => { clearLocalVaultSession(); setPinUnlocked(false); }}
    triggerForcePin={storageMode==="local" && !isPinEnabled()} />;
}

function MainApp({ user, logout, storageMode, onChangeStorage, dark, toggleDark, onLock, triggerForcePin }) {
  const tm = theme(dark);
  // Feature 5: Show PIN setup prompt for local mode
  useEffect(() => { if (triggerForcePin) setShowForcePinSetup(true); }, [triggerForcePin]);
  const [accounts, setAccounts]   = useState([]);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showIosInstall, setShowIosInstall] = useState(false);
  
  // PWA install prompt
  useEffect(() => {
    const handler = e => { e.preventDefault(); setInstallPrompt(e); setShowInstall(true); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Safari/iPhone does not support the normal install prompt, so show a lightweight Add to Home Screen tip.
  useEffect(() => {
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
    const dismissed = localStorage.getItem("finvault_ios_install_dismissed") === "1";
    setShowIosInstall(Boolean(isIOS && !isStandalone && !dismissed));
  }, []);


  // Feature 2 & 6: Auto-lock on inactivity
  useEffect(() => {
    if (!isPinEnabled()) return;
    // Track user activity
    const onActivity = () => updateLastActive();
    ["click","touchstart","keydown","scroll"].forEach(e => window.addEventListener(e, onActivity, {passive:true}));
    // Check every 30 seconds if should auto-lock
    const interval = setInterval(() => {
      if (shouldAutoLock()) { onLock(); }
    }, 30000);
    return () => {
      ["click","touchstart","keydown","scroll"].forEach(e => window.removeEventListener(e, onActivity));
      clearInterval(interval);
    };
  }, [onLock]);
  
  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === "accepted") { setInstallPrompt(null); setShowInstall(false); }
  };
  const [txnMap, setTxnMap]       = useState({});   // { accountId: [txns] }
  const [activeId, setActiveId]   = useState(null);
  // Local mode never needs loading - data is instant
  const [loadingAccs, setLoadingAccs] = useState(storageMode !== "local");
  
  // Safety timeout - never stuck on loading forever
  useEffect(() => {
    const timer = setTimeout(() => setLoadingAccs(false), 5000);
    return () => clearTimeout(timer);
  }, []);
  const [tab, setTab]             = useState("home");
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [showForcePinSetup, setShowForcePinSetup] = useState(false);
  const [sheet, setSheet]         = useState(null);
  const [editTxId, setEditTxId]   = useState(null);
  const [filter, setFilter]       = useState("all");
  const [historyFilters, setHistoryFilters] = useState(DEFAULT_HISTORY_FILTERS);
  const [delId, setDelId]         = useState(null);
  const [delAccId, setDelAccId]   = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [showAccForm, setShowAccForm] = useState(false);
  const [editAccId, setEditAccId] = useState(null);
  const [accForm, setAccForm]     = useState({ name:"", icon:"👤", color:"#4F46E5", type:"standard", phone:"" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [primaryBusyId, setPrimaryBusyId] = useState(null);
  const [showCVS, setShowCVS]     = useState(false);
  const [cvInput, setCVI]         = useState("");
  const [showExport, setShowExport] = useState(false);
  const [encryptedExportForm, setEncryptedExportForm] = useState({ password:"", confirm:"", busy:false });
  const [showBudget, setShowBudget] = useState(false);
  const [budgetForm, setBudgetForm] = useState({ monthly:"", categories:{} });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showFreeInfo, setShowFreeInfo] = useState(false);
  const [showSafetyInfo, setShowSafetyInfo] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ type: "Bug", message: "" });
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [feedbackNotice, setFeedbackNotice] = useState("");
  const [showRecurring, setShowRecurring] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => localStorage.getItem("finvault_onboarding_done") !== "1");
  const [recurringForm, setRecurringForm] = useState({ title:"", type:"expense", category:"Food", amount:"", frequency:"monthly", nextDate:today(), paymentMethod:"", note:"" });
  const [savingTx, setSavingTx]   = useState(false);
  const [localRefresh, setLocalRefresh] = useState(0);
  const refreshLocal = useCallback(() => { if(storageMode==="local") setLocalRefresh(r=>r+1); }, [storageMode]);

  // ── FEATURE 5: Auto-lock after 5 mins inactivity ────────────
  useEffect(() => {
    const checkAutoLock = () => {
      if (shouldAutoLock()) { onLock(); }
    };
    // Update last active on any user interaction
    const handleActivity = () => updateLastActive();
    window.addEventListener("touchstart", handleActivity);
    window.addEventListener("click", handleActivity);
    window.addEventListener("keydown", handleActivity);
    // Check every 60 seconds
    const interval = setInterval(checkAutoLock, 60000);
    updateLastActive(); // mark active on mount
    return () => {
      window.removeEventListener("touchstart", handleActivity);
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      clearInterval(interval);
    };
  }, [onLock]);

  // Schedule due date notifications
  useEffect(() => {
    if (!("Notification" in window)) return;
    const checkDueDates = () => {
      const today2 = new Date().toISOString().split("T")[0];
      const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate()+1);
      const tomorrowStr = tomorrow.toISOString().split("T")[0];
      Object.values(txnMap).flat().forEach(t => {
        if (t.txType==="lent" && t.dueDate && (t.dueDate===today2 || t.dueDate===tomorrowStr)) {
          const msg = t.dueDate===today2
            ? `⚠️ Payment from ${t.person||"someone"} is due TODAY — ${fmt(t.amount)}`
            : `🔔 Payment from ${t.person||"someone"} is due TOMORROW — ${fmt(t.amount)}`;
          if (Notification.permission === "granted") {
            new Notification("FinVault Reminder", { body: msg, icon: "/icons/icon-192.png" });
          }
        }
      });
    };
    if (Notification.permission === "default") {
      Notification.requestPermission().then(p => { if(p==="granted") checkDueDates(); });
    } else if (Notification.permission === "granted") {
      checkDueDates();
    }
  }, [txnMap]);

  const blankForm = pt => {
    if (pt==="lending") return { txType:"lent", note:"", amount:"", cashback:"", date:today(), dueDate:"" };
    if (pt==="trading") return { txType:"deposit", note:"", amount:"", date:today(), symbol:"", assetType:"Stocks", exchange:"NSE", qty:"", buyPrice:"", stopLoss:"", target:"" };
    if (isMetalProfile(pt)) return { txType:"buy", metalType:getProfileMetal(pt)||"Gold", grams:"", amount:"", pricePerGram:"", cashback:"", platform:"", note:"", date:today() };
    if (pt==="fdrd") return { txType:"active", depositType:"FD", bankName:"", depositName:"", amount:"", interestRate:"", maturityDate:"", expectedMaturityAmount:"", interestPayout:"On maturity", monthlyDeposit:"", totalMonths:"", installmentDay:"", actualReturnedAmount:"", closureDate:"", penalty:"", renewedAmount:"", note:"", date:today() };
    return { type:"expense", category:"Food", note:"", amount:"", date:today(), paymentMethod:"" };
  };
  const [form, setForm] = useState(blankForm("standard"));

  // ── Subscribe to accounts
  useEffect(() => {
    if (!storageMode) return;
    // Local Mode does not need Firebase user. Cloud modes do.
    if (storageMode !== "local" && !user) return;
    let unsub;
    let alive = true;
    setLoadingAccs(true);
    loadAccounts(user?.uid || "local", storageMode, accs => {
      if (!alive) return;
      setAccounts(accs);
      setLoadingAccs(false);
      if (accs.length > 0 && !accs.find(a=>a.id===activeId)) {
        const primaryProfile = accs.find(a => a.isPrimary === true);
        setActiveId((primaryProfile || accs[0]).id);
      }
    }).then(u => { unsub = u; }).catch(() => { if (alive) setLoadingAccs(false); });
    return () => { alive = false; unsub && unsub(); };
  }, [user, storageMode, localRefresh, activeId]);

  // ── Subscribe to transactions for all accounts
  const accountIds = accounts.map(a => a.id).join(",");
  useEffect(() => {
    if (!storageMode || !accounts.length) return;
    // Local Mode reads from IndexedDB/local vault. Cloud modes need Firebase user.
    if (storageMode !== "local" && !user) return;
    let alive = true;
    const unsubs = [];
    accounts.forEach(acc => {
      loadTransactions(user?.uid || "local", storageMode, acc.id, txns => {
        if (!alive) return;
        setTxnMap(prev => ({ ...prev, [acc.id]: txns }));
      }).then(u => unsubs.push(u));
    });
    return () => { alive = false; unsubs.forEach(u => u && u()); };
  }, [accountIds, accounts, storageMode, localRefresh, user]);
  const metalMigrationRunning = useRef(false);
  const migratedMetalLegacyIds = useRef(new Set());

  // ── Legacy Gold/Silver Combined migration ─────────────────────
  // Phase 48: The combined profile is removed from new profile creation.
  // Existing legacy data is split into the new Gold and Silver profiles so users
  // don't lose old records and don't need to manually recreate entries.
  useEffect(() => {
    if (!storageMode || !accounts.length || metalMigrationRunning.current) return;
    if (storageMode !== "local" && !user) return;

    const legacyAccounts = accounts.filter(a => a?.profileType === "goldsilver" && !migratedMetalLegacyIds.current.has(a.id));
    if (!legacyAccounts.length) return;

    const legacyTxnsReady = legacyAccounts.every(a => Object.prototype.hasOwnProperty.call(txnMap, a.id));
    if (!legacyTxnsReady) return;

    const migrateLegacyMetalProfiles = async () => {
      metalMigrationRunning.current = true;
      const storageUid = user?.uid || "local";
      let preferredActiveId = activeId;
      const targetIdsByType = {
        gold: accounts.find(a => a.profileType === "gold")?.id || null,
        silver: accounts.find(a => a.profileType === "silver")?.id || null
      };

      try {
        for (const legacy of legacyAccounts) {
          const legacyTxns = txnMap[legacy.id] || [];
          const goldTxns = legacyTxns.filter(t => (t.metalType || "Gold") === "Gold");
          const silverTxns = legacyTxns.filter(t => (t.metalType || "Gold") === "Silver");
          const groups = [
            { metal: "Gold", profileType: "gold", txns: goldTxns, icon: "🥇", color: "#CA8A04" },
            { metal: "Silver", profileType: "silver", txns: silverTxns, icon: "🥈", color: "#64748B" }
          ];

          const createdOrFound = {};
          for (const group of groups) {
            if (!group.txns.length) continue;

            const existingTarget = accounts.find(a =>
              a.profileType === group.profileType &&
              (a.migratedFromLegacyGoldSilverId === legacy.id || a.name === group.metal || a.name === `${legacy.name} - ${group.metal}`)
            ) || accounts.find(a => a.profileType === group.profileType);

            let targetId = targetIdsByType[group.profileType] || existingTarget?.id;
            if (!targetId) {
              const safeTarget = sanitizeAccountForStorage({
                name: accounts.some(a => a.profileType === group.profileType) ? `${legacy.name} - ${group.metal}` : group.metal,
                icon: group.icon,
                color: group.color,
                profileType: group.profileType,
                transactions: [],
                currentValue: 0,
                cvHistory: [],
                migratedFromLegacyGoldSilverId: legacy.id,
                migratedFromLegacyGoldSilverName: legacy.name || "Gold/Silver Combined"
              });
              targetId = await saveAccount(storageUid, storageMode, safeTarget);
            }
            targetIdsByType[group.profileType] = targetId;

            createdOrFound[group.profileType] = targetId;
            const existingTargetIds = new Set((txnMap[targetId] || []).map(t => t.id));
            for (const tx of group.txns) {
              const migratedId = existingTargetIds.has(tx.id) ? uid2() : tx.id;
              const migratedTx = sanitizeTransactionForStorage({
                ...tx,
                id: migratedId,
                metalType: group.metal,
                migratedFromLegacyGoldSilverId: legacy.id,
                migratedFromLegacyTransactionId: tx.id,
                migratedAt: new Date().toISOString()
              }, group.profileType);
              await saveTxn(storageUid, storageMode, targetId, migratedTx, group.profileType);
            }
          }

          await removeAccount(storageUid, storageMode, legacy.id);
          migratedMetalLegacyIds.current.add(legacy.id);

          const migratedPrimaryId = createdOrFound.gold || createdOrFound.silver || accounts.find(a => a.id !== legacy.id)?.id || null;
          if (legacy.isPrimary && migratedPrimaryId) {
            try { await setPrimaryAccount(storageUid, storageMode, migratedPrimaryId); }
            catch (err) { logWarn("Primary profile could not be preserved during Gold/Silver migration", err); }
          }

          if (activeId === legacy.id) {
            preferredActiveId = migratedPrimaryId;
          }
        }

        if (preferredActiveId !== activeId) setActiveId(preferredActiveId);
        refreshLocal();
      } catch (err) {
        logWarn("Legacy Gold/Silver migration failed", err);
        alert("Gold/Silver migration could not complete. Your old combined profile was not removed unless the transfer succeeded. Please try again or export a backup.");
      } finally {
        metalMigrationRunning.current = false;
      }
    };

    migrateLegacyMetalProfiles();
  }, [accounts, txnMap, storageMode, user, activeId, refreshLocal]);

  const active = accounts.find(a => a.id === activeId);
  const primaryAccount = accounts.find(a => a.isPrimary === true) || null;
  const txns   = txnMap[activeId] || [];
  const pt     = active?.profileType || "standard";

  const summary = (() => {
    if (!active) return {};
    if (pt==="standard") {
      const inc = txns.filter(t=>t.type==="income").reduce((s,t)=>s+t.amount,0);
      const exp = txns.filter(t=>t.type==="expense").reduce((s,t)=>s+t.amount,0);
      return { income:inc, expense:exp, balance:inc-exp };
    }
    if (pt==="lending") {
      return buildLendingSummary(txns);
    }
    if (pt==="trading") {
      const dep = txns.filter(t=>t.txType==="deposit").reduce((s,t)=>s+t.amount,0);
      const wdr = txns.filter(t=>t.txType==="withdraw").reduce((s,t)=>s+t.amount,0);
      const prf = txns.filter(t=>t.txType==="profit").reduce((s,t)=>s+t.amount,0);
      const lss = txns.filter(t=>t.txType==="loss").reduce((s,t)=>s+t.amount,0);
      const div = txns.filter(t=>t.txType==="dividend").reduce((s,t)=>s+t.amount,0);
      const invested = dep - wdr;
      const cv = active.currentValue || 0;
      const unrealised = cv > 0 ? cv - (invested - div) : 0;
      return { dep, wdr, prf, lss, div, invested, cv, unrealised, pnl:prf-lss+div };
    }
    if (isMetalProfile(pt)) {
      const fixedMetal = getProfileMetal(pt);
      const metals = ["Gold", "Silver"].reduce((acc, metal) => {
        const items = txns.filter(t => (t.metalType || fixedMetal || "Gold") === metal);
        const boughtGrams = items.filter(t=>t.txType==="buy").reduce((s,t)=>s+Number(t.grams||0),0);
        const soldGrams = items.filter(t=>t.txType==="sell").reduce((s,t)=>s+Number(t.grams||0),0);
        const adjGrams = items.filter(t=>t.txType==="adjustment").reduce((s,t)=>s+Number(t.grams||0),0);
        const buyValue = items.filter(t=>t.txType==="buy").reduce((s,t)=>s+Number(t.amount||0),0);
        const soldValue = items.filter(t=>t.txType==="sell").reduce((s,t)=>s+Number(t.amount||0),0);
        const cashback = items.filter(t=>t.txType==="buy" || t.txType==="cashback").reduce((s,t)=>s+Number(t.cashback||0)+Number(t.txType==="cashback"?t.amount||0:0),0);
        // Cashback is informational only; do not deduct it from purchase cost or average cost.
        const purchaseValue = Math.max(0, buyValue);
        const avgCost = boughtGrams > 0 ? buyValue / boughtGrams : 0;
        const holdingGrams = boughtGrams - soldGrams + adjGrams;
        const holdingCost = Math.max(0, holdingGrams * avgCost);
        const realised = soldValue - (soldGrams * avgCost);
        acc[metal.toLowerCase()] = { boughtGrams, soldGrams, holdingGrams, buyValue, soldValue, cashback, netCost: purchaseValue, avgCost, realised, holdingCost, holdingValue: holdingCost };
        return acc;
      }, {});
      const activeMetals = fixedMetal ? [fixedMetal] : ["Gold", "Silver"];
      const totalInvested = activeMetals.reduce((s,m)=>s+(metals[m.toLowerCase()]?.netCost||0),0);
      const totalHoldingCost = activeMetals.reduce((s,m)=>s+(metals[m.toLowerCase()]?.holdingCost||0),0);
      const totalSold = activeMetals.reduce((s,m)=>s+(metals[m.toLowerCase()]?.soldValue||0),0);
      const totalCashback = activeMetals.reduce((s,m)=>s+(metals[m.toLowerCase()]?.cashback||0),0);
      const goldRealised = metals.gold?.realised||0;
      const silverRealised = metals.silver?.realised||0;
      const realised = activeMetals.reduce((s,m)=>s+(metals[m.toLowerCase()]?.realised||0),0);
      return { ...metals, fixedMetal, activeMetals, metalLabel:getMetalProfileLabel(pt), totalInvested, totalHoldingCost, totalSold, totalCashback, goldRealised, silverRealised, realised };
    }
    if (pt==="fdrd") {
      const invested = txns.reduce((s,t)=>s+Number(t.amount||0),0);
      const expectedMaturity = txns.reduce((s,t)=>s+Number(t.expectedMaturityAmount||0),0);
      const actualReturned = txns.reduce((s,t)=>s+Number(t.actualReturnedAmount||0),0);
      const penalties = txns.reduce((s,t)=>s+Number(t.penalty||0),0);
      const activeStatuses = ["active", "renewed"];
      const closedTxns = txns.filter(t=>!activeStatuses.includes(t.txType || "active"));
      const activeTxns = txns.filter(t=>activeStatuses.includes(t.txType || "active"));
      const closedInvested = closedTxns.reduce((s,t)=>s+Number(t.amount||0),0);
      const expectedGain = expectedMaturity - invested;
      const actualGain = actualReturned - closedInvested - penalties;
      const expectedReturnPct = invested > 0 ? (expectedGain / invested) * 100 : 0;
      const actualReturnPct = closedInvested > 0 ? (actualGain / closedInvested) * 100 : 0;
      const monthlyCommitment = activeTxns.filter(t=>t.depositType==="RD").reduce((s,t)=>s+Number(t.monthlyDeposit||0),0);
      const upcoming = [...activeTxns].filter(t=>t.maturityDate).sort((a,b)=>new Date(a.maturityDate)-new Date(b.maturityDate)).slice(0,3);
      const banks = txns.reduce((acc,t)=>{ const bank=t.bankName||"Other"; if(!acc[bank]) acc[bank]={count:0,invested:0,expected:0}; acc[bank].count++; acc[bank].invested+=Number(t.amount||0); acc[bank].expected+=Number(t.expectedMaturityAmount||0); return acc; },{});
      return { invested, expectedMaturity, actualReturned, penalties, expectedGain, actualGain, expectedReturnPct, actualReturnPct, activeCount:activeTxns.length, closedCount:closedTxns.length, monthlyCommitment, upcoming, banks };
    }
    return {};
  })();

  // ── Account actions
  const handleCreateAcc = async () => {
    const safeAccount = sanitizeAccountForStorage({
      name: accForm.name,
      icon: accForm.icon,
      color: accForm.color,
      profileType: accForm.type,
      phone: accForm.type === "lending" ? accForm.phone : "",
      isPrimary: accounts.length === 0,
      transactions: [], currentValue: 0, cvHistory: []
    });
    if (!safeAccount.name) return alert("Enter a valid profile name.");
    setSavingProfile(true);
    try {
      const id = await saveAccount(user.uid, storageMode, safeAccount);
      await saveActivityLog(user.uid, storageMode, id, createActivityLog({
        action:"profile_created",
        entityType:"profile",
        account:{ ...safeAccount, id },
        after:{ ...safeAccount, id },
        message:"Profile created"
      }));
      setActiveId(id);
      setAccForm({ name:"", icon:"👤", color:"#4F46E5", type:"standard", phone:"" });
      setShowAccForm(false);
      refreshLocal();
    } finally {
      setSavingProfile(false);
    }
  };

  const handleUpdateAcc = async () => {
    const editingAcc = accounts.find(a => a.id === editAccId) || active;
    const safeAccount = sanitizeAccountForStorage({
      ...editingAcc,
      id: editAccId,
      name: accForm.name,
      icon: accForm.icon,
      color: accForm.color,
      profileType: editingAcc?.profileType || accForm.type,
      phone: editingAcc?.profileType === "lending" ? accForm.phone : ""
    });
    if (!safeAccount.name) return alert("Enter a valid profile name.");
    setSavingProfile(true);
    try {
      await saveAccount(user.uid, storageMode, safeAccount);
      await saveActivityLog(user.uid, storageMode, safeAccount.id, createActivityLog({
        action:"profile_updated",
        entityType:"profile",
        account:safeAccount,
        before:editingAcc,
        after:safeAccount,
        message:"Profile updated"
      }));
      setEditAccId(null); setShowAccForm(false);
      refreshLocal();
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSetPrimaryProfile = async id => {
    const target = accounts.find(a => a.id === id);
    if (!target || target.isPrimary || primaryBusyId) return;
    setPrimaryBusyId(id);
    try {
      await setPrimaryAccount(user.uid, storageMode, id);
      setAccounts(prev => prev.map(acc => ({ ...acc, isPrimary: acc.id === id })));
      setActiveId(id);
      await saveActivityLog(user.uid, storageMode, id, createActivityLog({
        action:"primary_profile_set",
        entityType:"profile",
        account:{ ...target, isPrimary:true },
        before:{ ...target, isPrimary:Boolean(target.isPrimary) },
        after:{ ...target, isPrimary:true },
        message:`Primary profile set: ${target.name}`
      }));
      refreshLocal();
    } catch (err) {
      logWarn("Primary profile update failed", err);
      alert(err?.message || "Could not update the primary profile. Please try again.");
    } finally {
      setPrimaryBusyId(null);
    }
  };

  const handleDeleteAcc = async id => {
    if (!(await verifySensitiveAction("delete this profile"))) return;
    const deletedAccount = accounts.find(a => a.id === id);
    const remainingAccounts = accounts.filter(a => a.id !== id);
    await logGlobalActivity({
      action:"profile_deleted",
      entityType:"profile",
      account:deletedAccount || { id },
      before:deletedAccount || { id },
      message:`Profile deleted: ${deletedAccount?.name || "Profile"}`
    });
    await removeAccount(user.uid, storageMode, id);

    if (deletedAccount?.isPrimary && remainingAccounts.length) {
      try {
        await setPrimaryAccount(user.uid, storageMode, remainingAccounts[0].id);
        setAccounts(prev => prev
          .filter(acc => acc.id !== id)
          .map(acc => ({ ...acc, isPrimary: acc.id === remainingAccounts[0].id })));
      } catch (err) {
        logWarn("Primary profile fallback could not be saved after deletion", err);
      }
    } else {
      setAccounts(prev => prev.filter(acc => acc.id !== id));
    }

    if (activeId === id) setActiveId(remainingAccounts[0]?.id || null);
    setDelAccId(null);
    refreshLocal();
  };

  // ── Transaction actions
  const openAdd = () => { setForm(blankForm(pt)); setEditTxId(null); setSheet("tx"); };
  const openQuickStandard = (type) => {
    if (pt !== "standard") return openAdd();
    setForm({ ...blankForm("standard"), type, category: STD_CATS[type][0].l });
    setEditTxId(null);
    setSheet("tx");
  };
  const openEdit = t => {
    if (pt==="standard") setForm({
      type:t.type, category:t.category, note:t.note||"",
      amount:String(t.amount), date:t.date,
      paymentMethod:t.paymentMethod||""
    });
    else if (pt==="lending") setForm({
      txType:t.txType,
      note:t.note||"", amount:String(t.amount), date:t.date,
      dueDate:t.dueDate||"", cashback:String(t.cashback || (t.txType==="profit" ? t.amount : ""))
    });
    else if (pt==="trading") setForm({
      txType:t.txType, note:t.note||"", amount:String(t.amount), date:t.date,
      symbol:t.symbol||"", assetType:t.assetType||"Stocks",
      exchange:t.exchange||"NSE", qty:t.qty||"",
      buyPrice:t.buyPrice||"", stopLoss:t.stopLoss||"", target:t.target||""
    });
    else if (isMetalProfile(pt)) setForm({
      txType:t.txType||"buy", metalType:getProfileMetal(pt)||t.metalType||"Gold", grams:String(t.grams||""),
      note:t.note||"", amount:String(t.amount||""), pricePerGram:String(t.pricePerGram||""),
      cashback:String(t.cashback||""), platform:t.platform||"", date:t.date
    });
    else setForm({
      txType:t.txType||"active", depositType:t.depositType||"FD", bankName:t.bankName||"", depositName:t.depositName||"",
      note:t.note||"", amount:String(t.amount||""), interestRate:String(t.interestRate||""), date:t.date,
      maturityDate:t.maturityDate||"", expectedMaturityAmount:String(t.expectedMaturityAmount||""), interestPayout:t.interestPayout||"On maturity",
      monthlyDeposit:String(t.monthlyDeposit||""), totalMonths:String(t.totalMonths||""), installmentDay:String(t.installmentDay||""),
      actualReturnedAmount:String(t.actualReturnedAmount||""), closureDate:t.closureDate||"", penalty:String(t.penalty||""), renewedAmount:String(t.renewedAmount||"")
    });
    setEditTxId(t.id); setSheet("tx");
  };

  const saveTx = async () => {
    let amt = sanitizeAmount(form.amount);
    const metalTxType = form.txType || "buy";
    const metalGrams = sanitizeGram(form.grams);
    const enteredMetalPrice = sanitizeAmount(form.pricePerGram);

    if (isMetalProfile(pt)) {
      // Amount is the source of truth for Gold/Silver. Only fall back to
      // grams × price/g when Amount is empty or zero.
      if ((!amt || amt <= 0) && metalGrams > 0 && enteredMetalPrice > 0) {
        amt = sanitizeAmount(metalGrams * enteredMetalPrice);
      }
      if (["buy", "sell", "adjustment"].includes(metalTxType) && metalGrams <= 0) {
        return alert("Enter valid grams for this Gold/Silver entry.");
      }
      if (["buy", "sell"].includes(metalTxType) && (!amt || amt <= 0)) {
        return alert("Enter a valid amount. If amount is not available, enter grams and price per gram.");
      }
    }
    if (pt === "fdrd" && (!amt || amt <= 0)) {
      const monthly = sanitizeAmount(form.monthlyDeposit);
      const months = sanitizeAmount(form.totalMonths, 1200, 0);
      if ((form.depositType || "FD") === "RD" && monthly > 0 && months > 0) amt = sanitizeAmount(monthly * months);
    }
    if (!amt || amt <= 0) return alert("Enter a valid amount.");
    const safetyWarning = sensitiveNoteMessage(form.note);
    if (safetyWarning) {
      alert(safetyWarning);
      return;
    }
    setSavingTx(true);
    try {
      let entry = { ...form, amount: amt, receipt: null };
      if (pt === "lending") {
        entry = {
          ...entry,
          // Lending profiles already represent one person/account, so avoid asking again per entry.
          person: active?.name || form.person || "Lending Profile",
          phone: active?.phone || form.phone || "",
          cashback: sanitizeAmount(form.cashback)
        };
        delete entry.status;
      }
      if (isMetalProfile(pt)) {
        const grams = metalGrams;
        const txType = metalTxType;
        const metalType = getProfileMetal(pt) || form.metalType || "Gold";
        const cashback = txType === "buy" ? sanitizeAmount(form.cashback) : 0;
        // Save price/g based on the saved amount so dashboard average cost stays correct.
        // Use the entered price/g only when amount cannot derive it.
        const derivedPricePerGram = grams > 0 && amt > 0 ? sanitizeAmount(amt / grams) : 0;
        const pricePerGram = derivedPricePerGram || enteredMetalPrice;
        entry = { ...entry, txType, metalType, grams, pricePerGram, cashback, platform: form.platform || "" };
      }
      if (pt === "fdrd") {
        const invested = sanitizeAmount(amt);
        const expected = sanitizeAmount(form.expectedMaturityAmount);
        const actual = sanitizeAmount(form.actualReturnedAmount);
        const penalty = sanitizeAmount(form.penalty);
        const expectedReturnPct = invested > 0 && expected > 0 ? sanitizePercentage(((expected - invested) / invested) * 100) : 0;
        const actualReturnPct = invested > 0 && actual > 0 ? sanitizePercentage(((actual - invested - penalty) / invested) * 100) : 0;
        entry = {
          ...entry,
          txType: form.txType || "active",
          depositType: form.depositType || "FD",
          bankName: form.bankName || "",
          depositName: form.depositName || "",
          interestRate: sanitizePercentage(form.interestRate),
          maturityDate: form.maturityDate || "",
          expectedMaturityAmount: expected,
          interestPayout: form.interestPayout || "On maturity",
          monthlyDeposit: sanitizeAmount(form.monthlyDeposit),
          totalMonths: sanitizeAmount(form.totalMonths, 1200, 0),
          installmentDay: sanitizeAmount(form.installmentDay, 31, 0),
          actualReturnedAmount: actual,
          closureDate: form.closureDate || "",
          penalty,
          renewedAmount: sanitizeAmount(form.renewedAmount),
          expectedReturnPct,
          actualReturnPct
        };
      }
      const beforeEntry = editTxId ? txns.find(t => t.id === editTxId) : null;
      const safeEntry = sanitizeTransactionForStorage(editTxId ? { ...entry, id: editTxId } : entry, pt);
      const savedId = await saveTxn(user.uid, storageMode, activeId, safeEntry, pt);
      await logActivity(activeId, {
        action: editTxId ? "entry_updated" : "entry_created",
        entityType:"entry",
        before: beforeEntry,
        after: { ...safeEntry, id: savedId },
        message: editTxId ? "Entry updated" : "Entry created"
      });
      setSheet(null);
      refreshLocal();
    } catch (error) {
      logError("FinVault save entry failed", error);
      alert(`Entry not saved: ${error?.message || "Please check the fields and try again."}`);
    } finally {
      setSavingTx(false);
    }
  };

  const handleDelTx = async id => {
    if (!(await verifySensitiveAction("delete this entry"))) return;
    const deletedEntry = txns.find(t => t.id === id);
    await removeTxn(user.uid, storageMode, activeId, id);
    await logActivity(activeId, {
      action:"entry_deleted",
      entityType:"entry",
      before:deletedEntry || { id },
      after:deletedEntry || { id },
      message:"Entry deleted"
    });
    setDelId(null); setSheet(null);
    refreshLocal();
  };

  const saveCV = async () => {
    const v = sanitizeAmount(cvInput);
    if (!v || v < 0) return;
    const entry = { value:v, date:today(), id:uid2() };
    const newHistory = [entry, ...(active.cvHistory||[])].slice(0,90);
    await saveCurrentValue(user.uid, storageMode, activeId, v, newHistory);
    setShowCVS(false); setCVI("");
    refreshLocal();
  };

  const sendWhatsApp = (person, amount, note, phone) => {
    const safetyWarning = sensitiveNoteMessage(note);
    if (safetyWarning) {
      alert(`${safetyWarning} The reminder was not opened.`);
      return;
    }
    const msg = encodeURIComponent(
      `Hi ${person} 👋\n\nFriendly reminder: you have a pending payment of *${fmtFull(amount)}* to me.\n${note?`\nNote: ${note}\n`:""}\nPlease settle at your earliest convenience. 🙏\n\n— Sent via ${APP_NAME}`
    );
    const clean = (phone||"").replace(/[^0-9]/g,"");
    const url = clean.length >= 10 ? `https://wa.me/${clean}?text=${msg}` : `https://wa.me/?text=${msg}`;
    openExternalPage(url);
  };


  const sendTopReminder = () => {
    if (pt !== "lending") return setTab("summary");
    const outstanding = Number(summary.outstanding || 0);
    const lastNote = [...txns].filter(t => t.txType === "lent" && t.note).sort((a,b)=>new Date(b.date)-new Date(a.date))[0]?.note || "";
    if (outstanding > 0) sendWhatsApp(active?.name || "Friend", outstanding, lastNote, active?.phone || "");
    else setTab("summary");
  };

  const filteredTxns = (() => {
    const q = String(historyFilters.search || "").trim().toLowerCase();
    const minAmount = historyFilters.minAmount !== "" ? Number(historyFilters.minAmount) : null;
    const maxAmount = historyFilters.maxAmount !== "" ? Number(historyFilters.maxAmount) : null;

    const matchesType = (t) => {
      if (filter === "all") return true;
      if (pt === "standard") return t.type === filter;
      return t.txType === filter;
    };

    const matchesSearch = (t) => {
      if (!q) return true;
      const haystack = [
        active?.name, active?.profileType, t.type, t.txType, t.category, t.note, t.person,
        t.phone, t.paymentMethod, t.status, t.symbol, t.assetType, t.exchange, t.date,
        t.depositType, t.bankName, t.depositName, t.interestPayout, t.maturityDate, t.closureDate,
        t.amount != null ? String(t.amount) : "", t.interestRate != null ? String(t.interestRate) : "", t.expectedMaturityAmount != null ? String(t.expectedMaturityAmount) : ""
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    };

    const matchesDate = (t) => {
      const d = t.date || "";
      if (historyFilters.dateFrom && d < historyFilters.dateFrom) return false;
      if (historyFilters.dateTo && d > historyFilters.dateTo) return false;
      return true;
    };

    const matchesAmount = (t) => {
      const amount = Number(t.amount || 0);
      if (minAmount !== null && !Number.isNaN(minAmount) && amount < minAmount) return false;
      if (maxAmount !== null && !Number.isNaN(maxAmount) && amount > maxAmount) return false;
      return true;
    };

    const list = txns.filter(t => matchesType(t) && matchesSearch(t) && matchesDate(t) && matchesAmount(t));
    const sort = historyFilters.sort || "newest";
    return [...list].sort((a,b) => {
      if (sort === "oldest") return new Date(a.date) - new Date(b.date);
      if (sort === "highest") return Number(b.amount || 0) - Number(a.amount || 0);
      if (sort === "lowest") return Number(a.amount || 0) - Number(b.amount || 0);
      return new Date(b.date) - new Date(a.date);
    });
  })();

  const isHistoryFiltered = filter !== "all" || Object.entries(historyFilters).some(([k,v]) => k === "sort" ? v !== "newest" : Boolean(v));
  const resetHistoryFilters = () => { setFilter("all"); setHistoryFilters(DEFAULT_HISTORY_FILTERS); };

  const grouped = (() => {
    const map={};
    filteredTxns.forEach(t=>{ if(!map[t.date]) map[t.date]=[]; map[t.date].push(t); });
    const entries = Object.entries(map);
    if (["highest","lowest"].includes(historyFilters.sort)) return entries;
    return entries.sort((a,b)=>new Date(b[0])-new Date(a[0]));
  })();

  const ac = active?.color || "#4F46E5";
  const navItems = [
    {id:"home",icon:"🏠",label:"Home"},
    {id:"history",icon:"📋",label:"History"},
    {id:"reports",icon:"📊",label:"Reports"},
    {id:"journal",icon:"📓",label:"Journal"},
    {id:"accounts",icon:"👥",label:"Profiles"}
  ];

  const showFab = ["home", "history"].includes(tab);
  const dismissIosInstall = () => { localStorage.setItem("finvault_ios_install_dismissed", "1"); setShowIosInstall(false); };

  const budgetStats = pt === "standard" ? buildBudgetStats(active, txns) : null;
  const heroBg = pt === "lending"
    ? "linear-gradient(135deg,#FB923C 0%,#F43F5E 58%,#BE123C 100%)"
    : pt === "trading"
      ? "linear-gradient(135deg,#312E81 0%,#6D28D9 58%,#111827 100%)"
      : isMetalProfile(pt)
        ? (pt === "silver" ? "linear-gradient(135deg,#64748B 0%,#B8C4D6 48%,#334155 100%)" : "linear-gradient(135deg,#B86A00 0%,#F7B733 46%,#8C4714 100%)")
        : pt === "fdrd"
          ? "linear-gradient(135deg,#0F766E 0%,#14B8A6 56%,#064E3B 100%)"
          : `linear-gradient(135deg,${ac} 0%,#4F46E5 56%,#0EA5E9 100%)`;
  const activeMetric = pt === "lending"
    ? `Net ${(summary.netPosition||0) >= 0 ? "+" : "−"}${fmt(summary.netPosition || 0)} · Receivable ${fmt(summary.outstandingReceivable || 0)} · Payable ${fmt(summary.outstandingPayable || 0)}`
    : pt === "trading"
      ? (summary.cv > 0 ? `Portfolio ${fmt(summary.cv)}` : `Invested ${fmt(summary.invested || 0)}`)
      : isMetalProfile(pt)
        ? (summary.fixedMetal ? `Holding ${fmtGram(summary[summary.fixedMetal.toLowerCase()]?.holdingGrams||0)} · Avg ${fmtFull(summary[summary.fixedMetal.toLowerCase()]?.avgCost||0)}/g` : `Holdings ${fmtGram(summary.gold?.holdingGrams||0)} gold / ${fmtGram(summary.silver?.holdingGrams||0)} silver`)
        : pt === "fdrd"
          ? `Invested ${fmt(summary.invested || 0)} · Expected ${Number(summary.expectedReturnPct||0).toFixed(2)}%`
          : `Balance ${fmt(summary.balance || 0)}`;
  const allTxns = Object.values(txnMap).flat();
  const recordsByProfile = accounts.map(a => ({
    id: a.id,
    name: a.name,
    type: a.profileType,
    count: (txnMap[a.id] || []).length
  }));
  const highestRecordProfile = recordsByProfile.reduce((best, item) => item.count > best.count ? item : best, { name: active?.name || "Current profile", count: 0 });
  const localVaultBytes = getLocalVaultSizeBytes();
  const localVaultStatus = getLocalVaultStatus();
  const freePlanStats = {
    profiles: accounts.length,
    records: allTxns.length,
    receipts: allTxns.filter(t => Boolean(t.receipt)).length,
    cloudMode: storageMode !== "local",
    localVaultBytes,
    activeProfileRecords: (txnMap[activeId] || []).length,
    maxProfileRecords: highestRecordProfile.count,
    maxProfileName: highestRecordProfile.name,
    localVaultEncrypted: localVaultStatus.encrypted,
    localVaultUnlocked: localVaultStatus.unlocked,
    localVaultProvider: localVaultStatus.provider,
    localVaultStorage: localVaultStatus.storage || "localStorage",
    localVaultIndexedDB: Boolean(localVaultStatus.indexedDB)
  };
  const logActivity = async (accountId, payload = {}) => {
    try {
      const account = accounts.find(a => a.id === accountId) || active || { id: accountId, name: "", profileType: pt };
      await saveActivityLog(user?.uid || "local_user", storageMode, accountId, createActivityLog({ ...payload, account }));
    } catch (error) {
      logWarn("FinVault activity log skipped", error);
    }
  };
  const logGlobalActivity = async (payload = {}) => {
    try {
      await saveGlobalActivityLog(user?.uid || "local_user", storageMode, createActivityLog({ ...payload, entityType: payload.entityType || "system" }));
    } catch (error) {
      logWarn("FinVault global activity log skipped", error);
    }
  };

  const exportJsonBackup = async () => {
    const activityLogs = await loadActivityLogsForExport(user?.uid || "local_user", storageMode, accounts);
    const safeData = sanitizeBackupForExport(accounts, txnMap);
    const backup={version:3,exportedAt:new Date().toISOString(),exportOnly:true,activityLogRetentionDays:30,activityLogs,...safeData};
    const blob=new Blob([JSON.stringify(backup,null,2)],{type:"application/json"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");a.href=url;a.download=`FinVault_backup_${new Date().toISOString().split("T")[0]}.json`;a.click();
    setTimeout(()=>URL.revokeObjectURL(url),1000);
  };

  const exportActivityLogJson = async () => {
    const activityLogs = await loadActivityLogsForExport(user?.uid || "local_user", storageMode, accounts);
    const rows = activityLogExportRows(accounts, activityLogs);
    const payload = { version:1, exportedAt:new Date().toISOString(), retentionDays:30, rows };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type:"application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `FinVault_activity_log_${new Date().toISOString().split("T")[0]}.json`; a.click();
    setTimeout(()=>URL.revokeObjectURL(url),1000);
  };
  const secureExportPDF = async () => {
    if (!(await verifySensitiveAction("export PDF report"))) return;
    await logGlobalActivity({ action:"export_pdf", message:"PDF report downloaded" });
    await exportToPDF(accounts, txnMap);
    setShowExport(false);
  };
  const secureExportExcel = async () => {
    if (!(await verifySensitiveAction("export Excel file"))) return;
    await logGlobalActivity({ action:"export_excel", message:"Excel export downloaded" });
    const activityLogs = await loadActivityLogsForExport(user?.uid || "local_user", storageMode, accounts);
    exportToExcel(accounts, txnMap, activityLogs);
    setShowExport(false);
  };
  const secureExportJson = async () => {
    if (!(await verifySensitiveAction("export JSON backup"))) return;
    await logGlobalActivity({ action:"export_json", message:"JSON backup downloaded" });
    await exportJsonBackup();
    setShowExport(false);
  };
  const secureExportEncryptedJson = async () => {
    if (!(await verifySensitiveAction("export encrypted backup"))) return;
    if (!isBackupEncryptionSupported()) return alert("Encrypted backup is not supported on this browser/device. Please update Chrome/Android System WebView and try again.");
    const password = encryptedExportForm.password || "";
    const confirm = encryptedExportForm.confirm || "";
    if (password.length < 8) return alert("Enter a backup password with at least 8 characters.");
    if (password !== confirm) return alert("Backup passwords do not match.");
    try {
      setEncryptedExportForm(f => ({ ...f, busy:true }));
      await logGlobalActivity({ action:"export_encrypted_backup", message:"Encrypted backup downloaded" });
      const activityLogs = await loadActivityLogsForExport(user?.uid || "local_user", storageMode, accounts);
      const safeData = sanitizeBackupForExport(accounts, txnMap);
      const backup = {
        version:4,
        encryptedSourceVersion:3,
        exportedAt:new Date().toISOString(),
        exportOnly:true,
        encryptedBackup:true,
        activityLogRetentionDays:30,
        activityLogs,
        ...safeData
      };
      await downloadEncryptedBackup(backup, password);
      setEncryptedExportForm({ password:"", confirm:"", busy:false });
      setShowExport(false);
    } catch (error) {
      logError("Encrypted backup failed", error);
      setEncryptedExportForm(f => ({ ...f, busy:false }));
      alert(error?.message || "Encrypted backup failed. Please try again.");
    }
  };
  const secureExportActivityJson = async () => {
    if (!(await verifySensitiveAction("export activity log"))) return;
    await logGlobalActivity({ action:"export_activity_log", message:"Activity log downloaded" });
    await exportActivityLogJson();
    setShowExport(false);
  };
  const cleanupReceipts = async (scope="active") => {
    const targetIds = scope === "all" ? accounts.map(a=>a.id) : [activeId];
    const items = targetIds.flatMap(accountId => (txnMap[accountId] || []).filter(t => t.receipt).map(t => ({ accountId, tx: t })));
    if (!items.length) return alert("No receipt images found to clean up.");
    const label = scope === "all" ? "all profiles" : (active?.name || "current profile");
    if (!window.confirm(`Remove receipt images from ${items.length} record(s) in ${label}?\n\nTip: export a backup first if you need these receipts later.`)) return;
    for (const {accountId, tx} of items) {
      const accountType = accounts.find(a => a.id === accountId)?.profileType || "standard";
      await saveTxn(user.uid, storageMode, accountId, { ...tx, receipt: null }, accountType);
    }
    refreshLocal();
    alert(`✅ Removed ${items.length} receipt image(s). Records are kept.`);
  };
  const pageSubtitle = pt === "lending"
    ? "Track money given, returned, and reminders."
    : pt === "trading"
      ? "Monitor deposits, P&L, and portfolio value."
      : isMetalProfile(pt)
        ? `Track digital ${getMetalProfileLabel(pt).toLowerCase()} grams, average cost, cashback, and sales.`
        : pt === "fdrd"
          ? "Track FD/RD invested amount, maturity value, returns, and closure."
          : "Track income, expenses, budget, and savings.";
  const openBudgetSettings = () => {
    const saved = getBudgetForMonth(active);
    setBudgetForm({
      monthly: saved.monthly ? String(saved.monthly) : "",
      categories: Object.fromEntries(Object.entries(saved.categories || {}).map(([k,v]) => [k, v ? String(v) : ""]))
    });
    setShowBudget(true);
  };
  const saveBudgetSettings = async () => {
    if (!active) return;
    const monthKey = currentMonthKey();
    const categories = {};
    Object.entries(budgetForm.categories || {}).forEach(([cat,val]) => {
      const n = toMoneyNumber(val);
      if (n > 0) categories[cat] = n;
    });
    await saveAccount(user?.uid || "local", storageMode, {
      ...active,
      budgets: {
        ...(active.budgets || {}),
        [monthKey]: { monthly: toMoneyNumber(budgetForm.monthly), categories }
      }
    });
    setShowBudget(false);
    refreshLocal();
  };

  const recurringRules = (active?.recurringRules || []).map(normalizeRecurringRule);
  const dueRecurringRules = pt === "standard" ? getDueRecurringRules(recurringRules) : [];

  const openRecurringSettings = () => {
    setRecurringForm({ title:"", type:"expense", category:"Food", amount:"", frequency:"monthly", nextDate:today(), paymentMethod:"", note:"" });
    setShowRecurring(true);
  };

  const saveRecurringRule = async () => {
    if (!active || pt !== "standard") return;
    const rule = normalizeRecurringRule(recurringForm);
    if (!rule.amount || rule.amount <= 0) return alert("Enter a valid recurring amount.");
    const rules = [...(active.recurringRules || []), rule];
    await saveAccount(user?.uid || "local", storageMode, { ...active, recurringRules: rules });
    setRecurringForm({ title:"", type:"expense", category:"Food", amount:"", frequency:"monthly", nextDate:today(), paymentMethod:"", note:"" });
    refreshLocal();
    alert("✅ Recurring rule saved.");
  };

  const deleteRecurringRule = async (ruleId) => {
    if (!active) return;
    const rules = (active.recurringRules || []).filter(r => r.id !== ruleId);
    await saveAccount(user?.uid || "local", storageMode, { ...active, recurringRules: rules });
    refreshLocal();
  };

  const postDueRecurring = async () => {
    if (!active || pt !== "standard") return;
    const due = getDueRecurringRules(active.recurringRules || []);
    if (!due.length) return alert("No recurring entries are due today.");
    const labels = due.map(r => `${r.title} (${fmtFull(r.amount)})`).join("\n");
    if (!window.confirm(`Post ${due.length} recurring entr${due.length===1?"y":"ies"}?

${labels}`)) return;
    const updatedRules = (active.recurringRules || []).map(rule => {
      const normalized = normalizeRecurringRule(rule);
      if (!due.some(d => d.id === normalized.id)) return rule;
      return { ...normalized, nextDate: addPeriod(normalized.nextDate, normalized.frequency), lastPostedAt: new Date().toISOString() };
    });
    for (const rule of due) {
      await saveTxn(user?.uid || "local", storageMode, activeId, ruleToTransaction(rule), "standard");
    }
    await saveAccount(user?.uid || "local", storageMode, { ...active, recurringRules: updatedRules });
    setShowRecurring(false);
    refreshLocal();
    alert(`✅ Posted ${due.length} recurring entr${due.length===1?"y":"ies"}.`);
  };



  const getFeedbackPlatform = () => {
    const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || "Web";
    return sanitizeFeedbackMessageInput(platform, 120) || "Web";
  };

  const submitFeedback = async () => {
    const message = sanitizeFeedbackMessage(feedbackForm.message, FIELD_LIMITS.feedback);
    const type = sanitizeFeedbackType(feedbackForm.type);
    if (!message) {
      setFeedbackNotice("Please enter feedback using letters, numbers, spaces, and basic punctuation only.");
      return;
    }
    setFeedbackBusy(true);
    setFeedbackNotice("");
    try {
      await saveFeedback(user?.uid || "local_user", storageMode, {
        type,
        message,
        appVersion: APP_VERSION,
        platform: getFeedbackPlatform()
      });
      setFeedbackForm({ type: "Bug", message: "" });
      setFeedbackNotice("✅ Thanks! Your feedback was submitted securely.");
    } catch (error) {
      setFeedbackNotice(error?.message || "Feedback could not be submitted. Please try again later.");
    } finally {
      setFeedbackBusy(false);
    }
  };

  const finishOnboarding = () => {
    localStorage.setItem("finvault_onboarding_done", "1");
    setShowOnboarding(false);
  };

  // ── Welcome / Loading
  // Local mode never shows loading spinner - data is instant
  if (loadingAccs && storageMode !== "local") return (
    <Wrap dark={dark} tm={tm}><GS dark={dark}/><LoadingVault message="Loading your vault…"/></Wrap>
  );

  if (accounts.length === 0) return (
    <Wrap dark={dark} tm={tm}><GS dark={dark}/>
      <div className="first-profile-screen" style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-start",minHeight:"100dvh",height:"100dvh",overflowY:"auto",WebkitOverflowScrolling:"touch",overscrollBehavior:"contain",padding:"calc(30px + env(safe-area-inset-top)) 32px calc(110px + env(safe-area-inset-bottom))",textAlign:"center"}}>
<div style={{marginBottom:16}}><BrandMark size={80}/></div>
        <div style={{fontSize:26,fontWeight:900,color:"#1E1B4B",marginBottom:6}}>{APP_NAME}</div>
        <div style={{fontSize:13,color:"#94A3B8",marginBottom:8}}>Welcome, {user.displayName||user.email}!</div>
        <div style={{fontSize:13,color:"#7C8DB5",marginBottom:32,lineHeight:1.7}}>Create your first profile to start tracking your money.</div>
        {PROFILE_TYPES.map(p=>(
          <div key={p.id} className="first-profile-type-card" style={{background:"white",borderRadius:16,padding:"14px 18px",display:"flex",alignItems:"center",gap:12,boxShadow:"0 2px 12px rgba(0,0,0,0.06)",width:"100%",maxWidth:720,marginBottom:8}}>
            <div style={{width:42,height:42,borderRadius:13,background:p.color+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>{p.icon}</div>
            <div><div style={{fontWeight:800,fontSize:14,color:"#1E1B4B"}}>{p.label}</div><div style={{fontSize:12,color:"#94A3B8"}}>{p.desc}</div></div>
          </div>
        ))}
        <button onClick={()=>{ setEditAccId(null); setAccForm({name:"",icon:"👤",color:"#4F46E5",type:"standard",phone:""}); setShowAccForm(true); }}
          className="first-profile-main-button" style={{background:"linear-gradient(135deg,#4F46E5,#7C3AED)",color:"white",border:"none",borderRadius:16,padding:"16px 40px",fontSize:16,fontWeight:900,cursor:"pointer",fontFamily:"inherit",marginTop:8,width:"100%",maxWidth:720}}>
          + Create First Profile
        </button>
        {storageMode === "local" ? (
          <button onClick={onChangeStorage} className="first-profile-cloud-button" style={{marginTop:12,background:"#EEF2FF",border:"1.5px solid #C7D2FE",borderRadius:14,color:"#4F46E5",fontSize:14,cursor:"pointer",fontFamily:"inherit",fontWeight:900,padding:"13px 18px",width:"100%",maxWidth:720}}>
            ☁️ Switch to Cloud Login / Change Storage
          </button>
        ) : (
          <button onClick={logout} style={{marginTop:12,background:"none",border:"none",color:"#64748B",fontSize:13,cursor:"pointer",fontFamily:"inherit",fontWeight:800}}>Sign Out</button>
        )}
        {storageMode === "local" && (
          <div style={{fontSize:11,color:"#94A3B8",fontWeight:700,lineHeight:1.5,marginTop:10}}>
            Local Mode does not use email login. Use the cloud switch above to sign in or create a cloud profile.
          </div>
        )}
      </div>
      {showAccForm && <AccFormSheet af={accForm} setAF={setAccForm} onSave={handleCreateAcc} onClose={()=>setShowAccForm(false)} isEdit={false} uploading={savingProfile}/>}
    </Wrap>
  );

  // Track activity for auto-lock
  const handleActivity = () => { if(isPinEnabled()) updateLastActive(); };
  const noteSafetyWarning = sensitiveNoteMessage(form.note);

  return (
    <Wrap dark={dark} tm={tm} onTouchStart={handleActivity} onMouseDown={handleActivity}>
      <GS dark={dark}/>
      <DesktopSidebar navItems={navItems} tab={tab} setTab={setTab} active={active} ac={ac} storageMode={storageMode} activeMetric={activeMetric} onAdd={openAdd} onExport={()=>setShowExport(true)} onSettings={()=>setTab("accounts")} />
      {showFab && <FloatingAddButton onClick={openAdd} color={ac} />}

      {/* ── HOME ── */}
      {tab==="home" && (
        <ScrollPage>
          <div className="desktop-page-title">
            <div style={{fontSize:26,fontWeight:900,color:"#1E1B4B",letterSpacing:-.6}}>Dashboard</div>
            <div style={{fontSize:13,color:"#64748B",fontWeight:800,marginTop:3}}>{pageSubtitle}</div>
          </div>
          <div className="hero-top premium-hero premium-hero-card" style={{background:heroBg,padding:"30px 18px 24px",borderRadius:30,margin:"12px 12px 0",position:"relative",overflow:"hidden",transform:"translateZ(0)",boxShadow:"0 24px 60px rgba(15,23,42,.18), inset 0 1px 0 rgba(255,255,255,.18)"}}>
            <div style={{position:"absolute",top:-50,right:-50,width:180,height:180,borderRadius:"50%",background:"rgba(255,255,255,0.13)",filter:"blur(.2px)",pointerEvents:"none"}}/>
            {/* Top row: profile pill + account/settings avatar */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18,position:"relative",zIndex:2}}>
              <button onClick={()=>setShowPicker(true)} style={{display:"flex",alignItems:"center",gap:10,background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.16)",borderRadius:18,padding:"8px 12px 8px 9px",cursor:"pointer",backdropFilter:"blur(8px)"}}>
                <ProfileAvatar acc={active} size={30} radius={10}/>
                <span style={{display:"flex",flexDirection:"column",alignItems:"flex-start",lineHeight:1.1}}>
                  <span style={{color:"white",fontWeight:900,fontSize:14,fontFamily:"inherit"}}>{active?.name}</span>
                  <span style={{color:"rgba(255,255,255,0.72)",fontSize:10,fontWeight:900,fontFamily:"inherit",textTransform:"uppercase",letterSpacing:.45}}>{getProfileTypeMeta(pt)?.label} profile{active?.isPrimary ? " · ★ primary" : ""}</span>
                </span>
                <span style={{color:"rgba(255,255,255,0.66)",fontSize:11,marginLeft:2}}>▾</span>
              </button>
              <button type="button" aria-label="Open profile and settings" title="Open profile and settings" onClick={()=>setShowUserMenu(true)} style={{width:40,height:40,borderRadius:"50%",border:"2px solid rgba(255,255,255,0.38)",background:"rgba(255,255,255,0.18)",cursor:"pointer",overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 10px 24px rgba(15,23,42,0.16)",position:"relative",zIndex:3}}>
                {user.photoURL
                  ? <img src={user.photoURL} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  : <span style={{color:"white",fontSize:16}}>👤</span>}
              </button>
            </div>

            {/* STANDARD */}
            {pt==="standard" && <>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.66)",fontWeight:800,letterSpacing:1.5,textTransform:"uppercase",marginBottom:4}}>Total Balance</div>
              <div style={{fontSize:42,fontWeight:900,color:"#fff",letterSpacing:-1.2,marginBottom:4}}>{fmt(summary.balance)}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.68)",fontWeight:700,marginBottom:18}}>May {new Date().getFullYear()} overview · {budgetStats?.budget?.monthly>0 ? `${Math.round(budgetStats.pct)}% budget used` : "Set a budget to control spending"}</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                <MiniCard label="↑ INCOME"  val={fmt(summary.income)}  vc="#6EE7B7"/>
                <MiniCard label="↓ EXPENSE" val={fmt(summary.expense)} vc="#FCA5A5"/>
                <MiniCard label="SAVED" val={fmt(summary.income-summary.expense)} vc={(summary.income-summary.expense)>=0?"#FDE68A":"#FCA5A5"}/>
              </div>
            </>}

            {/* LENDING */}
            {pt==="lending" && <>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.66)",fontWeight:800,letterSpacing:1.5,textTransform:"uppercase",marginBottom:4}}>Net Lending Position</div>
              <div style={{fontSize:36,fontWeight:900,color:"#fff",letterSpacing:-1,marginBottom:4}}>{(summary.netPosition||0) >= 0 ? "+" : "−"}{fmt(summary.netPosition || 0)}</div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:12}}>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.72)",fontWeight:750}}>
                  {(summary.netPosition||0) >= 0 ? "You should receive overall" : "You need to repay overall"}
                </div>
                {summary.outstandingReceivable>0 && <button type="button" onClick={sendTopReminder}
                  style={{border:"1px solid rgba(255,255,255,0.24)",borderRadius:999,padding:"7px 11px",background:"rgba(255,255,255,0.18)",color:"#6EE7B7",fontSize:12,fontWeight:900,fontFamily:"inherit",cursor:"pointer",boxShadow:"inset 0 1px 0 rgba(255,255,255,0.14)"}}>
                  💬 Reminder
                </button>}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                <MiniCard label="LENT OUT"  val={fmt(summary.lent)}     vc="#FCA5A5"/>
                <MiniCard label="RETURNED"  val={fmt(summary.returned)} vc="#6EE7B7"/>
                <MiniCard label="BORROWED"  val={fmt(summary.borrowed)} vc="#C4B5FD"/>
                <MiniCard label="REPAID"    val={fmt(summary.repaid)}   vc="#93C5FD"/>
                <MiniCard label="RECEIVABLE" val={fmt(summary.outstandingReceivable)} vc="#FDE68A"/>
                <MiniCard label="PAYABLE" val={fmt(summary.outstandingPayable)} vc="#FBCFE8"/>
                <MiniCard label="PROFIT"  val={fmt(summary.cashback || 0)} vc="#DDD6FE"/>
              </div>
            </>}

            {/* GOLD / SILVER */}
            {isMetalProfile(pt) && (()=>{
              const activeMetals = summary.activeMetals || ["Gold", "Silver"];
              const miniRows = activeMetals.flatMap(metal => {
                const data = summary[metal.toLowerCase()] || {};
                const color = metal === "Gold" ? "#FDE68A" : "#E0F2FE";
                return [
                  <MiniCard key={`${metal}-grams`} label={`${metal.toUpperCase()} GRAMS`} val={fmtGram(data.holdingGrams||0)} vc={color}/>,
                  <MiniCard key={`${metal}-avg`} label={`${metal.toUpperCase()} CURRENT AVG COST/G`} val={fmtFull(data.avgCost||0)} vc={color}/>,
                  <MiniCard key={`${metal}-holding`} label={`${metal.toUpperCase()} HOLDING COST`} val={fmtFull(data.holdingCost||0)} vc={color}/>,
                  <MiniCard key={`${metal}-realised`} label={`${metal.toUpperCase()} REALIZED P/L`} val={((data.realised||0)>=0?"+":"") + fmtFull(data.realised||0)} vc={(data.realised||0)>=0?"#6EE7B7":"#FCA5A5"}/>,
                ];
              });
              return <>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.66)",fontWeight:800,letterSpacing:1.5,textTransform:"uppercase",marginBottom:4}}>Digital {summary.metalLabel || "Gold/Silver"} Holdings</div>
                <div style={{fontSize:34,fontWeight:900,color:"#fff",letterSpacing:-1,marginBottom:4}}>{fmtFull(summary.totalHoldingCost || 0)}</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.72)",fontWeight:700,marginBottom:14}}>Remaining holding cost · current avg cost/g shown · sold value and cashback tracked separately</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  {miniRows}
                  <MiniCard label="SOLD VALUE" val={fmtFull(summary.totalSold||0)} vc="#93C5FD"/>
                  <MiniCard label="CASHBACK" val={fmtFull(summary.totalCashback||0)} vc="#C4B5FD"/>
                </div>
              </>;
            })()}


            {/* FD / RD */}
            {pt==="fdrd" && <>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.66)",fontWeight:800,letterSpacing:1.5,textTransform:"uppercase",marginBottom:4}}>FD/RD Dashboard</div>
              <div style={{fontSize:34,fontWeight:900,color:"#fff",letterSpacing:-1,marginBottom:4}}>{fmt(summary.invested || 0)}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.72)",fontWeight:700,marginBottom:14}}>Expected maturity {fmt(summary.expectedMaturity || 0)} · Expected return {Number(summary.expectedReturnPct||0).toFixed(2)}%</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <MiniCard label="EXPECTED GAIN" val={fmt(summary.expectedGain || 0)} vc="#A7F3D0"/>
                <MiniCard label="ACTUAL RETURN" val={fmt(summary.actualReturned || 0)} vc="#C4B5FD"/>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <MiniCard label="ACTIVE" val={String(summary.activeCount || 0)} vc="#BBF7D0"/>
                <MiniCard label="CLOSED" val={String(summary.closedCount || 0)} vc="#FED7AA"/>
              </div>
            </>}

            {/* TRADING */}
            {pt==="trading" && <>
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.6)",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",marginBottom:4}}>Current Portfolio Value</div>
                  <div style={{fontSize:36,fontWeight:900,color:"#fff",letterSpacing:-1}}>{summary.cv>0?fmt(summary.cv):"—"}</div>
                  {summary.cv>0 && <div style={{fontSize:12,color:summary.unrealised>=0?"#6EE7B7":"#FCA5A5",fontWeight:700,marginTop:3}}>Unrealised: {summary.unrealised>=0?"+":""}{fmt(summary.unrealised)}</div>}
                </div>
                <button onClick={()=>{setCVI(summary.cv>0?String(summary.cv):"");setShowCVS(true);}}
                  style={{background:"rgba(255,255,255,0.2)",border:"none",borderRadius:12,padding:"8px 12px",cursor:"pointer",flexShrink:0,marginTop:2}}>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.7)",fontWeight:700}}>UPDATE</div>
                  <div style={{fontSize:18}}>✏️</div>
                </button>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginTop:14}}>
                <MiniCard label="INVESTED"  val={fmt(summary.invested)} vc="#93C5FD"/>
                <MiniCard label="P&L"       val={(summary.pnl>=0?"+":"")+fmt(summary.pnl)} vc={summary.pnl>=0?"#6EE7B7":"#FCA5A5"}/>
                <MiniCard label="DIVIDEND"  val={fmt(summary.div)}      vc="#FDE68A"/>
              </div>
            </>}
          </div>

          <div style={{padding:"20px 16px 0"}}>
            <QuickActionPanel pt={pt} color={ac} onAdd={pt==="standard"?openQuickStandard:openAdd} onBudget={openBudgetSettings} onExport={()=>setShowExport(true)} onReports={()=>setTab("summary")} onReminder={sendTopReminder} onRecurring={openRecurringSettings}/>
            {pt==="standard" && <MoneyInsight summary={summary} budgetStats={budgetStats} color={ac}/>}
            {pt==="standard" && <BudgetSnapshot stats={budgetStats} color={ac} onEdit={openBudgetSettings}/>}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:16,fontWeight:800,color:"#1E1B4B"}}>Recent</div>
              <button onClick={()=>setTab("history")} style={{fontSize:12,color:ac,fontWeight:700,border:"none",background:"none",cursor:"pointer"}}>See all →</button>
            </div>
            {txns.length===0
              ? <EmptyState icon={getProfileTypeMeta(pt)?.icon||"💰"} msg="No transactions yet" sub="Start tracking by adding your first income, expense, lending, or trade entry." actionLabel="Add Transaction" onAction={openAdd}/>
              : [...txns].sort((a,b)=>new Date(b.date)-new Date(a.date)).slice(0,5).map(t=>(
                <TxnRow key={t.id} t={t} pt={pt} color={ac} onClick={()=>openEdit(t)}
                  onWhatsApp={pt==="lending"&&t.txType==="lent" ? ()=>sendWhatsApp(active?.name||t.person||"Friend",t.amount,t.note,active?.phone||t.phone) : null}/>
              ))}
          </div>
        </ScrollPage>
      )}

      {/* ── HISTORY ── */}
      {tab==="history" && (
        <ScrollPage>
          <div style={{padding:"52px 16px 16px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div>
                <div style={{fontSize:24,fontWeight:900,color:"#1E1B4B",letterSpacing:-.4}}>Transactions</div>
                <div style={{fontSize:12,color:"#94A3B8",fontWeight:700,marginTop:2}}>{filteredTxns.length} records · {active?.icon} {active?.name}</div>
              </div>
              <button onClick={openAdd} style={{border:"none",background:ac,color:"white",borderRadius:14,padding:"9px 13px",fontFamily:"inherit",fontWeight:900,cursor:"pointer",boxShadow:`0 10px 20px ${ac}30`}}>＋ Add</button>
            </div>
            <FilterBar pt={pt} filter={filter} setFilter={setFilter} color={ac}/>
            <TransactionFilters
              filters={historyFilters}
              setFilters={setHistoryFilters}
              color={ac}
              resultCount={filteredTxns.length}
              totalCount={txns.length}
              onReset={resetHistoryFilters}
            />
            {grouped.length===0
              ? <EmptyState icon="🔎" msg="No transactions found" sub={isHistoryFiltered ? "No records match your search or filters." : "Add your first transaction to start tracking."} actionLabel={isHistoryFiltered ? "Clear Filters" : "Add Transaction"} onAction={isHistoryFiltered ? resetHistoryFilters : openAdd}/>
              : <>
                <TxnDesktopTable
                  txns={filteredTxns}
                  pt={pt}
                  color={ac}
                  onEdit={openEdit}
                  onDelete={setDelId}
                  onWhatsApp={(t)=>sendWhatsApp(active?.name||t.person||"Friend",t.amount,t.note,active?.phone||t.phone)}
                />
                <div className="mobile-history-list">{grouped.map(([date,list])=>(
                  <div key={date} style={{marginBottom:18}}>
                    <div style={{fontSize:11,fontWeight:800,color:"#94A3B8",letterSpacing:1,textTransform:"uppercase",marginBottom:8,paddingLeft:4}}>
                      {new Date(date).toLocaleDateString("en-IN",{weekday:"short",day:"2-digit",month:"short",year:"numeric"})}
                    </div>
                    {list.map(t=>(
                      <TxnRow key={t.id} t={t} pt={pt} color={ac} onClick={()=>openEdit(t)} showDelete onDelete={()=>setDelId(t.id)}
                        onWhatsApp={pt==="lending"&&t.txType==="lent" ? ()=>sendWhatsApp(active?.name||t.person||"Friend",t.amount,t.note,active?.phone||t.phone) : null}/>
                    ))}
                  </div>
                ))}</div>
              </>}
          </div>
        </ScrollPage>
      )}

      {/* ── SUMMARY ── */}
      {tab==="summary" && (
        <ScrollPage>
          <div style={{padding:"52px 16px 16px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,gap:10}}>
              <div style={{minWidth:0}}>
                <button onClick={()=>setTab("home")} aria-label="Back to dashboard" style={{border:"none",background:"transparent",color:ac,fontSize:12,fontWeight:900,fontFamily:"inherit",cursor:"pointer",padding:"0 0 5px",display:"inline-flex",alignItems:"center",gap:4}}>
                  ← Dashboard
                </button>
                <div style={{fontSize:22,fontWeight:900,color:"#1E1B4B"}}>Summary</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                <button onClick={()=>setShowExport(true)} style={{background:ac+"18",border:`1.5px solid ${ac}44`,borderRadius:12,padding:"7px 12px",cursor:"pointer",fontFamily:"inherit",fontWeight:800,fontSize:12,color:ac,display:"flex",alignItems:"center",gap:5}}>
                  📥 Export
                </button>
                <button onClick={()=>setTab("home")} aria-label="Close summary" title="Close summary" style={{width:38,height:38,border:"none",borderRadius:12,background:"#F1F5F9",color:"#64748B",fontSize:20,fontWeight:900,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>
                  ×
                </button>
              </div>
            </div>
            {pt==="standard" && <>
              <HeroCard color={ac} label="Net Balance" main={fmtFull(summary.balance)}>
                <SR label="Income"  val={fmtFull(summary.income)}  color="#6EE7B7"/>
                <SR label="Expense" val={fmtFull(summary.expense)} color="#FCA5A5"/>
              </HeroCard>
              <CatBreakdown txns={txns} type="income"  color="#22C55E"/>
              <CatBreakdown txns={txns} type="expense" color="#EF4444"/>
              <BudgetSummaryCard stats={budgetStats} color={ac} onEdit={openBudgetSettings}/>
            </>}
            {pt==="lending" && <>
              <HeroCard color={ac} label="Net Lending Position" main={((summary.netPosition||0)>=0?"+":"−") + fmtFull(summary.netPosition||0)}>
                <SR label="Receivable" val={fmtFull(summary.outstandingReceivable)} color="#FCA5A5"/>
                <SR label="Payable"     val={fmtFull(summary.outstandingPayable)} color="#C4B5FD"/>
              </HeroCard>
              <div style={{background:"white",borderRadius:20,padding:18,marginBottom:14}}>
                <div style={{fontSize:14,fontWeight:800,color:"#1E1B4B",marginBottom:14}}>🤝 Lending Details</div>
                {[{l:"Total Lent Out",v:summary.lent,c:"#DC2626"},{l:"Total Returned",v:summary.returned,c:"#059669"},{l:"Outstanding Receivable",v:summary.outstandingReceivable,c:"#D97706",b:true},{l:"Receivable Status",text:summary.receivableStatus,c:"#D97706"},{l:"Total Borrowed",v:summary.borrowed,c:"#7C3AED"},{l:"Total Repaid",v:summary.repaid,c:"#0284C7"},{l:"Outstanding Payable",v:summary.outstandingPayable,c:"#DB2777",b:true},{l:"Payable Status",text:summary.payableStatus,c:"#DB2777"},{l:"Net Position",v:summary.netPosition,c:(summary.netPosition||0)>=0?"#059669":"#DC2626",b:true},{l:"Profit Amount",v:summary.cashback||0,c:"#7C3AED"}].map(r=>(
                  <div key={r.l} style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid #F1F5F9"}}>
                    <span style={{fontSize:13,fontWeight:r.b?800:600,color:r.b?"#1E1B4B":"#64748B"}}>{r.l}</span>
                    <span style={{fontSize:13,fontWeight:800,color:r.c}}>{r.text || fmtFull(r.v)}</span>
                  </div>
                ))}
              </div>
              {/* Overdue alerts */}
              {(() => {
                const td = new Date().toISOString().split("T")[0];
                const od = txns.filter(t=>t.txType==="lent" && t.dueDate && t.dueDate < td);
                return od.length > 0 ? (
                  <div style={{background:"#FEF2F2",border:"2px solid #FECACA",borderRadius:16,padding:"12px 14px",marginBottom:14}}>
                    <div style={{fontSize:13,fontWeight:800,color:"#DC2626",marginBottom:8}}>⚠️ Overdue ({od.length})</div>
                    {od.map(t=>(
                      <div key={t.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #FECACA"}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:700,color:"#1E1B4B"}}>{t.person||"Unknown"}</div>
                          <div style={{fontSize:11,color:"#DC2626",fontWeight:600}}>Due: {t.dueDate}</div>
                        </div>
                        <div style={{fontWeight:900,color:"#DC2626"}}>{fmt(t.amount)}</div>
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}
              <PersonBreakdown txns={txns} active={active} onWhatsApp={sendWhatsApp}/>
            </>}
            {isMetalProfile(pt) && (()=>{
              const activeMetals = summary.activeMetals || ["Gold", "Silver"];
              const rows = activeMetals.flatMap(metal => {
                const key = metal.toLowerCase();
                const data = summary[key] || {};
                const color = metal === "Gold" ? "#CA8A04" : "#64748B";
                return [
                  {l:`${metal} grams`,v:fmtGram(data.holdingGrams||0),c:color},
                  {l:`${metal} current avg cost/g`,v:fmtFull(data.avgCost||0),c:color,b:true},
                  {l:`${metal} holding cost`,v:fmtFull(data.holdingCost||0),c:color,b:true},
                  {l:`${metal} realized P/L`,v:((data.realised||0)>=0?"+":"")+fmtFull(data.realised||0),c:(data.realised||0)>=0?"#16A34A":"#EF4444",b:true},
                ];
              }).concat([
                {l:"Total purchase value",v:fmtFull(summary.totalInvested||0),c:"#CA8A04"},
                {l:"Total sold value",v:fmtFull(summary.totalSold||0),c:"#2563EB"},
                {l:"Remaining holding cost",v:fmtFull(summary.totalHoldingCost||0),c:"#0F766E",b:true},
                {l:"Cashback received",v:fmtFull(summary.totalCashback||0),c:"#7C3AED"},
              ]);
              const firstMetal = activeMetals[0] || "Gold";
              const firstData = summary[firstMetal.toLowerCase()] || {};
              return <>
                <HeroCard color={ac} label={`${summary.metalLabel || "Gold/Silver"} Remaining Holding Cost`} main={fmtFull(summary.totalHoldingCost || 0)}>
                  <SR label="Current avg cost/g" val={fmtFull(firstData.avgCost || 0)} color="#FDE68A"/>
                  <SR label="Holding grams" val={fmtGram(firstData.holdingGrams || 0)} color="#E0F2FE"/>
                  <SR label="Sold value" val={fmtFull(summary.totalSold || 0)} color="#BFDBFE"/>
                </HeroCard>
                <div style={{background:tm.card,borderRadius:20,padding:18,marginBottom:14}}>
                  <div style={{fontSize:14,fontWeight:800,color:tm.text,marginBottom:14}}>🪙 Holdings Summary</div>
                  {rows.map(r=>(
                    <div key={r.l} style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:`1px solid ${tm.border}`}}>
                      <span style={{fontSize:13,fontWeight:r.b?900:700,color:tm.textSub}}>{r.l}</span>
                      <span style={{fontSize:13,fontWeight:900,color:r.c}}>{r.v}</span>
                    </div>
                  ))}
                  <div style={{fontSize:11,color:tm.textSub,fontWeight:700,lineHeight:1.5,marginTop:12}}>Tracking only. FinVault does not buy/sell gold or silver and does not provide investment advice.</div>
                </div>
              </>;
            })()}
            {pt==="fdrd" && <>
              <HeroCard color={ac} label="FD/RD Overview" main={fmtFull(summary.invested || 0)}>
                <SR label="Expected Return" val={`${Number(summary.expectedReturnPct||0).toFixed(2)}%`} color="#A7F3D0"/>
                <SR label="Actual Return" val={`${Number(summary.actualReturnPct||0).toFixed(2)}%`} color="#C4B5FD"/>
              </HeroCard>
              <div style={{background:tm.card,borderRadius:20,padding:18,marginBottom:14}}>
                <div style={{fontSize:14,fontWeight:800,color:tm.text,marginBottom:14}}>🏦 FD/RD Return Summary</div>
                {[
                  {l:"Total invested",v:fmtFull(summary.invested||0),c:"#0F766E"},
                  {l:"Expected maturity value",v:fmtFull(summary.expectedMaturity||0),c:"#4F46E5"},
                  {l:"Expected gain",v:fmtFull(summary.expectedGain||0),c:(summary.expectedGain||0)>=0?"#16A34A":"#EF4444"},
                  {l:"Expected return %",v:`${Number(summary.expectedReturnPct||0).toFixed(2)}%`,c:"#0F766E",b:true},
                  {l:"Actual returned",v:fmtFull(summary.actualReturned||0),c:"#7C3AED"},
                  {l:"Actual gain",v:fmtFull(summary.actualGain||0),c:(summary.actualGain||0)>=0?"#16A34A":"#EF4444"},
                  {l:"Actual return %",v:`${Number(summary.actualReturnPct||0).toFixed(2)}%`,c:"#7C3AED",b:true},
                  {l:"Monthly RD commitment",v:fmtFull(summary.monthlyCommitment||0),c:"#EA580C"},
                ].map(r=>(
                  <div key={r.l} style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:`1px solid ${tm.border}`}}>
                    <span style={{fontSize:13,fontWeight:r.b?900:700,color:tm.textSub}}>{r.l}</span>
                    <span style={{fontSize:13,fontWeight:900,color:r.c}}>{r.v}</span>
                  </div>
                ))}
                <div style={{fontSize:11,color:tm.textSub,fontWeight:700,lineHeight:1.5,marginTop:12}}>Tracking only. FinVault does not provide banking, deposit, tax, or investment advice.</div>
              </div>
              {!!summary.upcoming?.length && <div style={{background:tm.card,borderRadius:20,padding:18,marginBottom:14}}>
                <div style={{fontSize:14,fontWeight:800,color:tm.text,marginBottom:12}}>⏰ Upcoming Maturity</div>
                {summary.upcoming.map(t=>(
                  <div key={t.id} style={{display:"flex",justifyContent:"space-between",gap:12,padding:"10px 0",borderBottom:`1px solid ${tm.border}`}}>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:900,color:tm.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.depositName || t.bankName || t.depositType}</div>
                      <div style={{fontSize:11,fontWeight:700,color:tm.textSub}}>{t.maturityDate}</div>
                    </div>
                    <div style={{fontSize:13,fontWeight:900,color:"#0F766E"}}>{fmtFull(t.expectedMaturityAmount || t.amount || 0)}</div>
                  </div>
                ))}
              </div>}
            </>}

            {pt==="trading" && <>
              <HeroCard color={ac} label="Portfolio Overview" main={summary.cv>0?fmtFull(summary.cv):"Set value ✏️"}>
                <SR label="Invested" val={fmtFull(summary.invested)} color="#93C5FD"/>
                <SR label="Net P&L"  val={(summary.pnl>=0?"+":"")+fmtFull(summary.pnl)} color={summary.pnl>=0?"#6EE7B7":"#FCA5A5"}/>
              </HeroCard>
              {/* Investment Flow */}
              <div style={{background:"white",borderRadius:20,padding:18,marginBottom:14}}>
                <div style={{fontSize:14,fontWeight:800,color:"#1E1B4B",marginBottom:14}}>💼 Investment Flow</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                  {[{label:"Total Deposited",val:summary.dep,color:"#059669",bg:"#F0FDF4",icon:"⬇️"},{label:"Total Withdrawn",val:summary.wdr,color:"#DC2626",bg:"#FEF2F2",icon:"⬆️"},{label:"Dividend Earned",val:summary.div,color:"#0284C7",bg:"#EFF6FF",icon:"💵"},{label:"Net Balance",val:summary.dep-summary.wdr+summary.div,color:(summary.dep-summary.wdr+summary.div)>=0?"#16A34A":"#DC2626",bg:"#F8FAFF",icon:"⚖️"}].map(r=>(
                    <div key={r.label} style={{background:r.bg,borderRadius:14,padding:"12px 14px"}}>
                      <div style={{fontSize:16,marginBottom:4}}>{r.icon}</div>
                      <div style={{fontSize:11,color:"#94A3B8",fontWeight:700,marginBottom:3}}>{r.label}</div>
                      <div style={{fontSize:16,fontWeight:900,color:r.color}}>{fmtFull(r.val)}</div>
                    </div>
                  ))}
                </div>
                <div style={{background:"#F8FAFF",borderRadius:12,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:13,fontWeight:700,color:"#64748B"}}>Balance Invested (Dep − Wdr)</span>
                  <span style={{fontSize:14,fontWeight:900,color:ac}}>{fmtFull(summary.invested)}</span>
                </div>
              </div>
              <div style={{background:"white",borderRadius:20,padding:18,marginBottom:14}}>
                <div style={{fontSize:14,fontWeight:800,color:"#1E1B4B",marginBottom:14}}>📊 Detailed Breakdown</div>
                {[{l:"Total Deposited",v:summary.dep,c:"#059669"},{l:"Total Withdrawn",v:summary.wdr,c:"#DC2626"},{l:"Net Invested",v:summary.invested,c:"#0284C7",b:true},{l:"Dividend Received",v:summary.div,c:"#0284C7"},{l:"Booked Profit",v:summary.prf,c:"#16A34A"},{l:"Booked Loss",v:summary.lss,c:"#EF4444"},{l:"Net P&L (incl. Div)",v:summary.pnl,c:summary.pnl>=0?"#16A34A":"#EF4444",b:true},...(summary.cv>0?[{l:"Current Portfolio Value",v:summary.cv,c:ac,b:true},{l:"Unrealised Gain/Loss",v:summary.unrealised,c:summary.unrealised>=0?"#16A34A":"#EF4444",b:true}]:[])].map(r=>(
                  <div key={r.l} style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid #F1F5F9"}}>
                    <span style={{fontSize:12,fontWeight:r.b?800:600,color:r.b?"#1E1B4B":"#64748B",flex:1,paddingRight:8}}>{r.l}</span>
                    <span style={{fontSize:13,fontWeight:800,color:r.c,flexShrink:0}}>{fmtFull(r.v)}</span>
                  </div>
                ))}
              </div>
              {(active?.cvHistory||[]).length>0 && (
                <div style={{background:"white",borderRadius:20,padding:18,marginBottom:14}}>
                  <div style={{fontSize:14,fontWeight:800,color:"#1E1B4B",marginBottom:14}}>📅 Value History</div>
                  {(active.cvHistory||[]).slice(0,10).map((h,i)=>(
                    <div key={h.id} style={{display:"flex",justifyContent:"space-between",padding:"9px 0",borderBottom:"1px solid #F1F5F9"}}>
                      <span style={{fontSize:13,color:"#64748B",fontWeight:600}}>{fmtDate(h.date)}{i===0?" 🟢":""}</span>
                      <span style={{fontSize:13,fontWeight:800,color:ac}}>{fmtFull(h.value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>}
            {txns.length===0 && <EmptyState icon="📊" msg="Reports need more data" sub="Add a few transactions to unlock breakdowns and insights." actionLabel="Add Transaction" onAction={openAdd}/>}
          </div>
        </ScrollPage>
      )}

      {/* ── PROFILES ── */}
      {tab==="accounts" && (
        <ScrollPage>
          <div style={{padding:"52px 16px 16px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <div>
                <div style={{fontSize:22,fontWeight:900,color:tm.text}}>Profiles</div>
                <div style={{fontSize:12,color:"#94A3B8",marginTop:2}}>
                  {storageMode==="local"?"📱 Stored on this device":storageMode==="gdrive"?"🟢 Saved to your Google Drive":"☁️ Synced to FinVault Cloud"}
                  <button onClick={onChangeStorage} style={{marginLeft:8,fontSize:10,color:"#4F46E5",fontWeight:700,border:"none",background:"none",cursor:"pointer",textDecoration:"underline"}}>Change</button>
                </div>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setShowExport(true)} style={{background:"#F1F5F9",border:"none",borderRadius:12,padding:"8px 12px",cursor:"pointer",fontFamily:"inherit",fontWeight:700,fontSize:12,color:"#64748B"}}>📥 Export</button>
                <button onClick={()=>{setEditAccId(null);setAccForm({name:"",icon:"👤",color:"#4F46E5",type:"standard",phone:""});setShowAccForm(true);}}
                  style={{background:ac,color:"white",border:"none",borderRadius:12,padding:"8px 16px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>+ New</button>
              </div>
            </div>
            <div style={{background:storageMode==="local"?"#ECFDF5":"#FFF7ED",border:`1.5px solid ${storageMode==="local"?"#BBF7D0":"#FED7AA"}`,borderRadius:18,padding:"13px 14px",marginBottom:14,display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:38,height:38,borderRadius:14,background:storageMode==="local"?"#DCFCE7":"#FFEDD5",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>{storageMode==="local"?"✅":"☁️"}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:900,color:storageMode==="local"?"#047857":"#C2410C"}}>{storageMode==="local"?"Free-safe local mode active":"Cloud sync is optional"}</div>
                <div style={{fontSize:11,color:"#64748B",fontWeight:700,marginTop:2}}>{storageMode==="local"?"No Firebase database usage for your records on this device.":"Use local mode for zero database usage, or keep cloud sync for backup/multi-device."}</div>
              </div>
              <button onClick={()=>setShowFreeInfo(true)} style={{border:"none",background:"white",borderRadius:12,padding:"9px 12px",fontSize:12,fontWeight:900,color:"#4F46E5",cursor:"pointer",fontFamily:"inherit"}}>Limits</button>
            </div>
            {accounts.length > 0 && (
              <div style={{background:dark?"#111827":"#FFFFFF",border:`1.5px solid ${dark?"#334155":"#E2E8F0"}`,borderRadius:18,padding:"14px",marginBottom:14,boxShadow:dark?"none":"0 6px 18px rgba(15,23,42,.05)"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                  <div style={{width:38,height:38,borderRadius:13,background:dark?"rgba(245,158,11,.16)":"#FFF7ED",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>⭐</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:900,color:tm.text}}>Primary Profile</div>
                    <div style={{fontSize:11,color:tm.textSub,fontWeight:700,marginTop:2,lineHeight:1.4}}>FinVault opens this profile by default. You can still switch anytime from the Dashboard profile dropdown.</div>
                  </div>
                </div>
                <select aria-label="Primary profile" value={primaryAccount?.id || ""} disabled={Boolean(primaryBusyId)} onChange={e=>handleSetPrimaryProfile(e.target.value)}
                  style={{width:"100%",boxSizing:"border-box",padding:"12px 13px",borderRadius:13,border:`2px solid ${dark?"#334155":"#E8EDFF"}`,background:dark?"#0F172A":"#F8FAFF",color:tm.text,fontFamily:"inherit",fontSize:13,fontWeight:850,outline:"none",cursor:primaryBusyId?"wait":"pointer"}}>
                  {!primaryAccount && <option value="">Choose primary profile</option>}
                  {accounts.map(acc=><option key={acc.id} value={acc.id}>{acc.icon} {acc.name} · {getProfileTypeMeta(acc.profileType)?.label}</option>)}
                </select>
                {!primaryAccount && <div style={{fontSize:10.5,color:"#D97706",fontWeight:800,marginTop:7}}>No primary profile is set yet. Until you choose one, FinVault opens the first available profile.</div>}
              </div>
            )}
            {accounts.map(acc=>{
              const ptI=getProfileTypeMeta(acc.profileType);
              const at=txnMap[acc.id]||[];
              let bal=0;
              if(acc.profileType==="standard") bal=at.reduce((s,t)=>t.type==="income"?s+t.amount:s-t.amount,0);
              if(acc.profileType==="lending")  { const ls=buildLendingSummary(at); bal=ls.netPosition; }
              if(acc.profileType==="trading")  bal=acc.currentValue||at.filter(t=>t.txType==="deposit").reduce((s,t)=>s+t.amount,0)-at.filter(t=>t.txType==="withdraw").reduce((s,t)=>s+t.amount,0)+at.filter(t=>t.txType==="profit").reduce((s,t)=>s+t.amount,0)-at.filter(t=>t.txType==="loss").reduce((s,t)=>s+t.amount,0)+at.filter(t=>t.txType==="dividend").reduce((s,t)=>s+t.amount,0);
              if(isMetalProfile(acc.profileType)) bal=(getProfileMetal(acc.profileType)?[getProfileMetal(acc.profileType)]:["Gold","Silver"]).reduce((total,metal)=>{
                const items=at.filter(t=>(t.metalType||getProfileMetal(acc.profileType)||"Gold")===metal);
                const bought=items.filter(t=>t.txType==="buy").reduce((s,t)=>s+Number(t.grams||0),0);
                const sold=items.filter(t=>t.txType==="sell").reduce((s,t)=>s+Number(t.grams||0),0);
                const adjusted=items.filter(t=>t.txType==="adjustment").reduce((s,t)=>s+Number(t.grams||0),0);
                const buyValue=items.filter(t=>t.txType==="buy").reduce((s,t)=>s+Number(t.amount||0),0);
                const avg=bought>0?buyValue/bought:0;
                return total + Math.max(0,(bought-sold+adjusted)*avg);
              },0);
              if(acc.profileType==="fdrd") bal=at.reduce((s,t)=>s+Number(t.amount||0),0);
              const isAct=acc.id===activeId;
              return (
                <div key={acc.id} onClick={()=>{setActiveId(acc.id);setTab("home");}}
                  style={{background:"white",borderRadius:20,padding:"16px",marginBottom:12,border:`2px solid ${isAct?acc.color:"transparent"}`,cursor:"pointer",boxShadow:"0 2px 12px rgba(0,0,0,0.06)"}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{width:52,height:52,borderRadius:16,overflow:"hidden",flexShrink:0,background:acc.color+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26}}>
                      {acc.icon}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                        <span style={{fontWeight:800,fontSize:15,color:"#1E1B4B"}}>{acc.name}</span>
                        <span style={{background:ptI?.color+"22",color:ptI?.color,fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:999}}>{ptI?.icon} {ptI?.label}</span>
                        {acc.isPrimary && <span style={{background:"#FFF7ED",color:"#B45309",border:"1px solid #FED7AA",fontSize:10,fontWeight:900,padding:"2px 8px",borderRadius:999}}>★ PRIMARY</span>}
                        {isAct && <span style={{background:acc.color,color:"white",fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:999}}>ACTIVE</span>}
                      </div>
                      <div style={{fontSize:12,color:"#94A3B8",marginTop:3}}>{at.length} transactions</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:16,fontWeight:900,color:bal>=0?"#16A34A":"#DC2626"}}>{fmt(bal)}</div>
                      <div style={{fontSize:11,color:"#94A3B8"}}>{acc.profileType==="lending"?"outstanding":isMetalProfile(acc.profileType)?"holding cost":acc.profileType==="fdrd"?"invested":"balance"}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,marginTop:14,paddingTop:12,borderTop:"1px solid #F1F5F9"}}>
                    <button disabled={acc.isPrimary || Boolean(primaryBusyId)} onClick={e=>{e.stopPropagation();handleSetPrimaryProfile(acc.id);}}
                      style={{flex:1,padding:"8px 6px",background:acc.isPrimary?"#FFF7ED":"#F8FAFF",border:"none",borderRadius:10,cursor:acc.isPrimary?"default":primaryBusyId?"wait":"pointer",fontFamily:"inherit",fontWeight:800,fontSize:11.5,color:acc.isPrimary?"#B45309":"#64748B",opacity:primaryBusyId&&primaryBusyId!==acc.id?0.8:1}}>
                      {primaryBusyId===acc.id?"Saving…":acc.isPrimary?"★ Primary":"☆ Set Primary"}
                    </button>
                    <button onClick={e=>{e.stopPropagation();setEditAccId(acc.id);setAccForm({name:acc.name,icon:acc.icon,color:acc.color,type:acc.profileType,phone:acc.phone||""});setShowAccForm(true);}}
                      style={{flex:1,padding:"8px 6px",background:"#F8FAFF",border:"none",borderRadius:10,cursor:"pointer",fontFamily:"inherit",fontWeight:700,fontSize:11.5,color:"#64748B"}}>✏️ Edit</button>
                    <button onClick={e=>{e.stopPropagation();setDelAccId(acc.id);}}
                      style={{flex:1,padding:"8px 6px",background:"#FEE2E2",border:"none",borderRadius:10,cursor:"pointer",fontFamily:"inherit",fontWeight:700,fontSize:11.5,color:"#DC2626"}}>🗑️ Delete</button>
                  </div>
                </div>
              );
            })}
            <div style={{background:"white",borderRadius:16,padding:14,marginTop:8,border:"1.5px solid #E2E8F0"}}>
              <div style={{fontSize:12,color:"#94A3B8",fontWeight:700,marginBottom:10}}>⚙️ Storage Settings</div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                <div style={{fontSize:28}}>{storageMode==="local"?"📱":storageMode==="gdrive"?"🟢":"☁️"}</div>
                <div>
                  <div style={{fontSize:13,fontWeight:800,color:"#1E1B4B"}}>{storageMode==="local"?"This Device Only":storageMode==="gdrive"?"My Google Drive":"FinVault Cloud"}</div>
                  <div style={{fontSize:11,color:"#94A3B8"}}>Current storage location</div>
                </div>
                <button onClick={onChangeStorage} style={{marginLeft:"auto",background:"#EEF2FF",border:"none",borderRadius:10,padding:"7px 12px",cursor:"pointer",fontFamily:"inherit",fontWeight:800,fontSize:12,color:"#4F46E5"}}>Change</button>
              </div>
            </div>
            {storageMode!=="local" && <button onClick={logout} style={{width:"100%",padding:"14px",background:"#FEE2E2",border:"none",borderRadius:16,cursor:"pointer",fontFamily:"inherit",fontWeight:800,fontSize:14,color:"#DC2626",marginTop:8}}>
              🚪 Sign Out
            </button>}
          </div>
        </ScrollPage>
      )}

      {/* BOTTOM NAV */}
      <div className="bottom-nav premium-bottom-nav" style={{position:"fixed",bottom:0,left:"50%",transform:"translateX(-50%)",width:"100%",maxWidth:430,background:tm.nav,borderTop:`1px solid ${tm.border}`,display:"flex",zIndex:100}}>
        {navItems.map(({id,label})=>{
          const selected = tab === id || (tab === "summary" && id === "reports");
          return (
            <button className={selected?"bottom-nav-item active":"bottom-nav-item"} key={id} onClick={()=>setTab(id)} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"8px 0 12px",border:"none",background:"transparent",cursor:"pointer"}}>
              <span className="bottom-nav-icon" style={{transform:selected?"translateY(-3px)":"none",transition:"transform 0.18s ease",display:"flex",alignItems:"center",justifyContent:"center",width:40,height:31,borderRadius:999,background:selected?`linear-gradient(135deg,${ac},#7C3AED)`:"transparent",color:selected?"white":tm.textSub,boxShadow:selected?`0 12px 22px ${ac}42, inset 0 1px 0 rgba(255,255,255,.38)`:"none"}}><LineIcon name={navIconName(id,label)} size={20} color="currentColor"/></span>
              <span style={{fontSize:9,fontWeight:900,color:selected?ac:tm.textSub,fontFamily:"inherit"}}>{label}</span>
            </button>
          );
        })}
      </div>

      {/* PROFILE PICKER */}
      {showPicker && (
        <Sheet onClose={()=>setShowPicker(false)} title="Switch Profile">
          <div style={{fontSize:16,fontWeight:900,color:tm.text,marginBottom:4}}>Switch Profile</div>
          <div style={{fontSize:11,fontWeight:800,color:tm.textSub,marginBottom:primaryAccount?8:16}}>Tap ×, drag handle, or tap outside to close.</div>
          {primaryAccount && <div style={{fontSize:10.5,fontWeight:900,color:dark?"#FCD34D":"#B45309",background:dark?"rgba(245,158,11,.12)":"#FFF7ED",border:`1px solid ${dark?"rgba(245,158,11,.25)":"#FED7AA"}`,borderRadius:10,padding:"7px 9px",marginBottom:12}}>★ Default on launch: {primaryAccount.name}</div>}
          {accounts.map(acc=>{
            const ptI=getProfileTypeMeta(acc.profileType);
            const isActiveProfile = acc.id === activeId;
            const pickerBg = isActiveProfile ? (dark ? acc.color + "26" : acc.color + "15") : (dark ? "#111827" : "#F8FAFF");
            const pickerBorder = isActiveProfile ? acc.color : (dark ? "#334155" : "transparent");
            return (
              <div key={acc.id} className={`profile-picker-card ${isActiveProfile ? "profile-picker-card-active" : ""}`} onClick={()=>{setActiveId(acc.id);setShowPicker(false);setTab("home");}}
                style={{display:"flex",alignItems:"center",gap:12,padding:"12px",borderRadius:14,marginBottom:8,background:pickerBg,cursor:"pointer",border:`1.5px solid ${pickerBorder}`}}>
                <div style={{width:44,height:44,borderRadius:13,overflow:"hidden",background:acc.color+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>
                  {acc.icon}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div className="profile-picker-name" style={{fontWeight:900,fontSize:14,color:dark?"#F8FAFC":"#1E1B4B",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{acc.name}</div>
                  <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginTop:2}}>
                    <div className="profile-picker-type" style={{fontSize:11,color:isActiveProfile?(dark?"#A7F3D0":ptI?.color):ptI?.color,fontWeight:800}}>{ptI?.icon} {ptI?.label}</div>
                    {acc.isPrimary && <span style={{fontSize:9.5,fontWeight:900,color:dark?"#FCD34D":"#B45309",background:dark?"rgba(245,158,11,.12)":"#FFF7ED",borderRadius:999,padding:"2px 6px"}}>★ Primary</span>}
                  </div>
                </div>
                {isActiveProfile && <span style={{color:dark?"#34D399":acc.color,fontSize:20,fontWeight:900}}>✓</span>}
              </div>
            );
          })}
          <button onClick={()=>{setShowPicker(false);setTab("accounts");}}
            style={{width:"100%",padding:"14px",marginTop:8,border:`2px dashed ${dark?"#334155":"#E2E8F0"}`,borderRadius:14,background:dark?"#0F172A":"transparent",cursor:"pointer",fontFamily:"inherit",fontWeight:800,fontSize:14,color:dark?"#CBD5E1":"#94A3B8"}}>
            + Manage Profiles
          </button>
          {storageMode!=="local" && <button onClick={()=>{logout();setShowPicker(false);}}
            style={{width:"100%",padding:"14px",border:"none",borderRadius:14,background:dark?"rgba(239,68,68,.16)":"#FEE2E2",cursor:"pointer",fontFamily:"inherit",fontWeight:900,fontSize:14,color:dark?"#FCA5A5":"#DC2626",marginTop:10}}>
            🚪 Sign Out
          </button>}
        </Sheet>
      )}

      {/* USER MENU */}
      {showUserMenu && (
        <Sheet onClose={()=>setShowUserMenu(false)} title="Profile and Settings">
          <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20,padding:"4px 0"}}>
            <div style={{width:56,height:56,borderRadius:18,overflow:"hidden",background:"#F1F5F9",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
              {user.photoURL ? <img src={user.photoURL} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <span style={{fontSize:28}}>👤</span>}
            </div>
            <div>
              <div style={{fontSize:16,fontWeight:900,color:tm.text}}>{user.displayName||user.email||"Local User"}</div>
              <div style={{fontSize:12,color:tm.textSub}}>{user.email}</div>
              <div style={{fontSize:11,color:"#4F46E5",fontWeight:700,marginTop:2}}>{storageMode==="local"?"📱 Local Storage":storageMode==="gdrive"?"🟢 Google Drive":"☁️ FinVault Cloud"}</div>
              <div style={{fontSize:10.5,color:"#94A3B8",fontWeight:800,marginTop:5}}>Tap ×, drag handle, or tap outside to close.</div>
            </div>
          </div>

          {/* Settings rows */}
          {[
            { icon:"👥", label:"Manage Profiles", action:()=>{ setTab("accounts"); setShowUserMenu(false); }},
            { icon:"🌙", label:"Dark Mode", action:()=>{toggleDark();}, right: <div style={{width:44,height:26,borderRadius:13,background:dark?"#4F46E5":"#E2E8F0",padding:3,cursor:"pointer",transition:"background 0.2s"}}><div style={{width:20,height:20,borderRadius:"50%",background:"white",transform:dark?"translateX(18px)":"translateX(0)",transition:"transform 0.2s",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}/></div>},
            { icon:"⏱️", label:`Auto-lock: ${getAutoLockMins()} min`, action:()=>{
              const opts=[1,2,5,10,15,30];
              const cur=getAutoLockMins();
              const next=opts[(opts.indexOf(cur)+1)%opts.length];
              setAutoLockMins(next);
              setShowUserMenu(false);
              setTimeout(()=>setShowUserMenu(true),50);
            }},
            { icon:"🔐", label: isPinEnabled()?"Change PIN":"Set PIN Lock", action:()=>{ setShowPinSetup(true); setShowUserMenu(false); }},
            { icon:"🔒", label:"Lock App Now", action:()=>{ onLock(); setShowUserMenu(false); }},
            { icon:"🔔", label:"Enable Reminders", action:()=>{ if("Notification" in window) Notification.requestPermission().then(p=>p==="granted"?alert("✅ Notifications enabled! You'll be reminded about due dates."):alert("❌ Permission denied. Enable in browser settings.")); setShowUserMenu(false); }},
            { icon:"📤", label:"Export & Backup", action:()=>{ setShowExport(true); setShowUserMenu(false); }},
            { icon:"🔁", label:"Recurring Transactions", action:()=>{ openRecurringSettings(); setShowUserMenu(false); }},
            { icon:"🚀", label:"Getting Started", action:()=>{ setShowOnboarding(true); setShowUserMenu(false); }},
            { icon:"🆓", label:"Free App & Limits", action:()=>{ setShowFreeInfo(true); setShowUserMenu(false); }},
            { icon:"🛡️", label:"Security & Scam Safety", action:()=>{ setShowSafetyInfo(true); setShowUserMenu(false); }},
            ...(storageMode === "firebase" ? [{ icon:"💬", label:"Help & Feedback", action:()=>{ setShowFeedback(true); setFeedbackNotice(""); setShowUserMenu(false); }}] : []),
            { icon:"📜", label:"Terms & Conditions", action:()=>{ openInternalPage("/terms"); setShowUserMenu(false); }},
            { icon:"🔒", label:"Privacy Policy", action:()=>{ openInternalPage("/privacy"); setShowUserMenu(false); }},
            { icon:"🧯", label:"Safety Web Page", action:()=>{ openInternalPage("/security"); setShowUserMenu(false); }},
            { icon:"🗑️", label:"Delete Account Info", action:()=>{ openInternalPage("/delete-account"); setShowUserMenu(false); }},
            ...(storageMode === "local" ? [{ icon:"☁️", label:"Switch to Cloud Login", action:()=>{ onChangeStorage(); setShowUserMenu(false); }}] : []),
            { icon:"⚙️", label:"Change Storage", action:()=>{ onChangeStorage(); setShowUserMenu(false); }},
          ].map(row=>(
            <button key={row.label} onClick={row.action}
              style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px",border:"none",borderRadius:14,background:tm.cardAlt,cursor:"pointer",fontFamily:"inherit",marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:20}}>{row.icon}</span>
                <span style={{fontSize:14,fontWeight:700,color:tm.text}}>{row.label}</span>
              </div>
              {row.right || <span style={{color:tm.textSub,fontSize:18}}>›</span>}
            </button>
          ))}

          {storageMode!=="local" && <button onClick={()=>{logout();setShowUserMenu(false);}}
            style={{width:"100%",padding:"15px",border:"none",borderRadius:14,background:"#FEE2E2",cursor:"pointer",fontFamily:"inherit",fontWeight:800,fontSize:15,color:"#DC2626",marginTop:4}}>
            🚪 Sign Out
          </button>}
        </Sheet>
      )}


      {/* FEEDBACK */}
      {showFeedback && storageMode === "firebase" && (
        <Sheet onClose={()=>setShowFeedback(false)} title="Help and Feedback">
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"flex-start",gap:12,marginBottom:16}}>
            <div>
              <div style={{fontSize:20,fontWeight:900,color:tm.text}}>Help & Feedback</div>
              <div style={{fontSize:12,color:tm.textSub,fontWeight:700,marginTop:4}}>Share a bug, idea, UI issue, or security concern.</div>
            </div>
          </div>

          <div style={{background:dark?"rgba(59,130,246,.10)":"#EFF6FF",border:`1.5px solid ${dark?"rgba(59,130,246,.28)":"#BFDBFE"}`,borderRadius:14,padding:"12px 14px",fontSize:12,fontWeight:800,color:dark?"#BFDBFE":"#1D4ED8",lineHeight:1.45,marginBottom:14}}>
            Feedback is available only for Cloud users so submissions can be reviewed securely. Financial records are not included.
          </div>

          <div style={{fontSize:11,fontWeight:900,color:tm.textSub,letterSpacing:1,textTransform:"uppercase",marginBottom:8}}>Feedback Type</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:8,marginBottom:14}}>
            {FEEDBACK_TYPES.map(type => {
              const selected = feedbackForm.type === type;
              return (
                <button key={type} onClick={()=>setFeedbackForm(f=>({...f,type}))}
                  style={{padding:"11px 10px",borderRadius:14,border:`2px solid ${selected?ac:(dark?"#334155":"#E8EDFF")}`,background:selected?(dark?ac+"30":"#EEF2FF"):(dark?"#111827":"#F8FAFF"),color:selected?ac:tm.text,fontFamily:"inherit",fontWeight:900,fontSize:12,cursor:"pointer"}}>
                  {type}
                </button>
              );
            })}
          </div>

          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
            <div style={{fontSize:11,fontWeight:900,color:tm.textSub,letterSpacing:1,textTransform:"uppercase"}}>Message</div>
            <div style={{fontSize:11,fontWeight:800,color:feedbackForm.message.length>=FIELD_LIMITS.feedback?"#DC2626":tm.textSub}}>{feedbackForm.message.length}/{FIELD_LIMITS.feedback}</div>
          </div>
          <textarea value={feedbackForm.message} maxLength={FIELD_LIMITS.feedback} placeholder="Example: Export button is not clear on my phone."
            onChange={e=>setFeedbackForm(f=>({...f,message:sanitizeFeedbackMessageInput(e.target.value, FIELD_LIMITS.feedback)}))}
            style={{width:"100%",minHeight:140,resize:"vertical",padding:"14px 16px",borderRadius:16,border:`2px solid ${dark?"#334155":"#E8EDFF"}`,background:dark?"#111827":"#F8FAFF",fontSize:14,fontFamily:"inherit",fontWeight:700,color:tm.text,outline:"none",lineHeight:1.5,boxSizing:"border-box",marginBottom:8}} />
          <div style={{fontSize:11.5,fontWeight:750,color:tm.textSub,lineHeight:1.45,marginBottom:14}}>
            Allowed: letters, numbers, spaces, and . , ? ! - _ only. URLs, HTML, scripts, file uploads, and special symbols are blocked.
          </div>

          {feedbackNotice && (
            <div style={{background:feedbackNotice.startsWith("✅")?(dark?"rgba(16,185,129,.12)":"#ECFDF5"):(dark?"rgba(245,158,11,.12)":"#FFFBEB"),border:`1.5px solid ${feedbackNotice.startsWith("✅")?(dark?"rgba(16,185,129,.35)":"#A7F3D0"):(dark?"rgba(245,158,11,.35)":"#FDE68A")}`,borderRadius:14,padding:"11px 13px",fontSize:12,fontWeight:850,color:feedbackNotice.startsWith("✅")?(dark?"#6EE7B7":"#047857"):(dark?"#FCD34D":"#92400E"),lineHeight:1.45,marginBottom:14}}>
              {feedbackNotice}
            </div>
          )}

          <button onClick={submitFeedback} disabled={feedbackBusy || !sanitizeFeedbackMessage(feedbackForm.message) || storageMode !== "firebase"}
            style={{width:"100%",padding:"16px",border:"none",borderRadius:16,background:ac,color:"white",fontFamily:"inherit",fontSize:15,fontWeight:900,cursor:(feedbackBusy || !sanitizeFeedbackMessage(feedbackForm.message) || storageMode !== "firebase")?"not-allowed":"pointer",opacity:(feedbackBusy || !sanitizeFeedbackMessage(feedbackForm.message) || storageMode !== "firebase")?0.58:1,boxShadow:"0 14px 30px rgba(79,70,229,.22)"}}>
            {feedbackBusy ? "Submitting..." : "Submit Feedback"}
          </button>
          <div style={{textAlign:"center",fontSize:10.5,fontWeight:800,color:"#94A3B8",marginTop:10}}>App version {APP_VERSION} • Feedback is private and never shown publicly.</div>
        </Sheet>
      )}

      {/* ACC FORM */}
      {showAccForm && <AccFormSheet af={accForm} setAF={setAccForm} onSave={editAccId?handleUpdateAcc:handleCreateAcc} onClose={()=>{setShowAccForm(false);setEditAccId(null);}} isEdit={!!editAccId} uploading={savingProfile}/>}

      {/* TX SHEET */}
      {sheet==="tx" && (
        <Sheet onClose={()=>setSheet(null)} showClose={false} title={editTxId?"Edit Entry":"New Entry"}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
            <div style={{fontSize:18,fontWeight:900,color:tm.text}}>{editTxId?"Edit Entry":"New Entry"}</div>
            <button onClick={()=>setSheet(null)} style={{width:36,height:36,borderRadius:12,border:"none",background:dark?"#1E293B":"#F1F5F9",color:dark?"#E2E8F0":"#0F172A",cursor:"pointer",fontSize:17}}>✕</button>
          </div>
          <div className="amount-panel" style={{background:dark?"linear-gradient(135deg,#111827,#0F172A)":"linear-gradient(135deg,#F8FAFF,#FFFFFF)",borderRadius:20,padding:"16px 18px",marginBottom:16,border:`2px solid ${dark?"#334155":"#E8EDFF"}`,boxShadow:dark?"0 14px 28px rgba(0,0,0,.22)":"0 10px 24px rgba(15,23,42,.04)"}}>
            <div style={{fontSize:11,fontWeight:900,color:tm.textSub,letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>Amount</div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:25,fontWeight:900,color:tm.textSub}}>₹</span>
              <input type="text" inputMode="decimal" placeholder="0.00" value={form.amount} onKeyDown={blockInvalidNumberKeys} onChange={e=>setForm(f=>({...f,amount:cleanMoneyInput(e.target.value)}))}
                style={{flex:1,border:"none",background:"transparent",fontSize:34,fontWeight:900,color:tm.text,outline:"none",fontFamily:"inherit",letterSpacing:-.5}}/>
            </div>
          </div>
          {pt==="standard" && <StdForm form={form} setForm={setForm} dark={dark}/>}
          {pt==="lending"  && <LendForm form={form} setForm={setForm} active={active} dark={dark}/>}
          {pt==="trading"  && <TradeForm form={form} setForm={setForm} dark={dark}/>}
          {isMetalProfile(pt) && <GoldSilverForm form={form} setForm={setForm} dark={dark} profileType={pt}/>}
          {pt==="fdrd" && <FDRDForm form={form} setForm={setForm} dark={dark}/>}
          <input placeholder="Note (optional)" value={form.note||""} maxLength={FIELD_LIMITS.note} onChange={e=>setForm(f=>({...f,note:sanitizeNoteInput(e.target.value, FIELD_LIMITS.note)}))}
            style={{width:"100%",padding:"13px 16px",borderRadius:14,border:`2px solid ${noteSafetyWarning?"#F59E0B":(dark?"#334155":"#E8EDFF")}`,background:dark?"#111827":"#F8FAFF",fontSize:14,fontFamily:"inherit",fontWeight:700,color:tm.text,outline:"none",marginBottom:noteSafetyWarning?8:14}}/>
          {noteSafetyWarning && (
            <div style={{background:"#FFF7ED",border:"1.5px solid #FED7AA",borderRadius:14,padding:"10px 12px",marginBottom:14,fontSize:11.5,fontWeight:800,color:"#C2410C",lineHeight:1.45}}>
              ⚠️ {noteSafetyWarning}
            </div>
          )}
          {pt==="standard" && (
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:800,color:"#94A3B8",letterSpacing:1,textTransform:"uppercase",marginBottom:8}}>Payment Method</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {PAYMENT_METHODS.map(m=>(
                  <button key={m} onClick={()=>setForm(f=>({...f,paymentMethod:m}))}
                    style={{padding:"8px 14px",borderRadius:20,border:"none",cursor:"pointer",fontFamily:"inherit",fontWeight:800,fontSize:12,background:form.paymentMethod===m?ac:(dark?"#1E293B":"#F1F5F9"),color:form.paymentMethod===m?"white":(dark?"#CBD5E1":"#64748B")}}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div style={{marginBottom:pt==="lending"?14:20}}>
            <div style={{fontSize:11,fontWeight:800,color:"#94A3B8",letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>Date</div>
            <input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))}
              style={{width:"100%",padding:"13px 16px",borderRadius:14,border:`2px solid ${dark?"#334155":"#E8EDFF"}`,background:dark?"#111827":"#F8FAFF",fontSize:14,fontFamily:"inherit",fontWeight:700,color:tm.text,outline:"none"}}/>
          </div>
          {pt==="lending" && ["lent","borrowed"].includes(form.txType) && (
            <div style={{marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:800,color:"#94A3B8",letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>Due Date (optional)</div>
              <input type="date" value={form.dueDate||""} onChange={e=>setForm(f=>({...f,dueDate:e.target.value}))}
                style={{width:"100%",padding:"13px 16px",borderRadius:14,border:`2px solid ${dark?"#334155":"#E8EDFF"}`,background:dark?"#111827":"#F8FAFF",fontSize:14,fontFamily:"inherit",fontWeight:700,color:tm.text,outline:"none"}}/>
              <div style={{fontSize:10.5,fontWeight:700,color:"#94A3B8",marginTop:6}}>Used for pending Lent Out or Borrowed reminders only.</div>
            </div>
          )}
          <div className="tx-save-footer">
            <button onClick={saveTx} disabled={savingTx}
              style={{width:"100%",padding:"17px",border:"none",borderRadius:16,cursor:"pointer",fontFamily:"inherit",fontWeight:900,fontSize:16,background:ac,color:"white",opacity:savingTx?0.7:1}}>
              {savingTx?"Saving…":editTxId?"Update Entry":"Save Entry"}
            </button>
            <span className="tx-save-footer-note">Review all details, then save. The button now stays at the bottom of the form.</span>
          </div>
          {editTxId && (
            <button onClick={()=>{setDelId(editTxId);setSheet(null);}}
              style={{width:"100%",padding:"13px",border:"none",borderRadius:16,cursor:"pointer",fontFamily:"inherit",fontWeight:700,fontSize:14,background:"transparent",color:"#EF4444",marginTop:8}}>
              Delete this entry
            </button>
          )}
        </Sheet>
      )}

      {/* PRODUCTIVITY: RECURRING TRANSACTIONS */}
      {showRecurring && (
        <Sheet onClose={()=>setShowRecurring(false)} showClose={false} title="Recurring Transactions">
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:14,marginBottom:18}}>
            <div>
              <div style={{fontSize:18,fontWeight:900,color:tm.text,marginBottom:4}}>🔁 Recurring Transactions</div>
              <div style={{fontSize:12,color:tm.textSub}}>Create salary, rent, EMI, SIP, subscription, and bill reminders.</div>
            </div>
            <button onClick={()=>setShowRecurring(false)} aria-label="Close recurring transactions" style={{width:42,height:42,borderRadius:14,border:"none",background:tm.cardAlt||"#F1F5F9",color:tm.text,fontSize:24,lineHeight:1,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>×</button>
          </div>
          {pt !== "standard" ? (
            <div style={{background:"#FFF7ED",border:"1.5px solid #FED7AA",borderRadius:16,padding:14,fontSize:12,fontWeight:800,color:"#C2410C",lineHeight:1.55}}>
              Recurring transactions are available for Standard income/expense profiles. Lending and Trading profiles should be recorded manually to avoid accidental duplicate records.
            </div>
          ) : <>
            {dueRecurringRules.length > 0 && (
              <button onClick={postDueRecurring} style={{width:"100%",border:"none",borderRadius:16,padding:"14px 16px",background:"linear-gradient(135deg,#EA580C,#F97316)",color:"white",fontFamily:"inherit",fontWeight:900,fontSize:14,marginBottom:14,cursor:"pointer"}}>
                Post {dueRecurringRules.length} Due Recurring Entr{dueRecurringRules.length===1?"y":"ies"}
              </button>
            )}
            <div style={{background:"#F8FAFF",border:"1.5px solid #E8EDFF",borderRadius:18,padding:14,marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:900,color:"#1E1B4B",marginBottom:12}}>Add recurring rule</div>
              <input placeholder="Title e.g. Rent, Salary, SIP" value={recurringForm.title} onChange={e=>setRecurringForm(f=>({...f,title:sanitizeTextInput(e.target.value, FIELD_LIMITS.title)}))} style={{width:"100%",boxSizing:"border-box",padding:"12px 14px",borderRadius:13,border:"2px solid #E8EDFF",background:"white",fontSize:14,fontFamily:"inherit",fontWeight:800,color:"#1E1B4B",outline:"none",marginBottom:9}}/>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9,marginBottom:9}}>
                <select value={recurringForm.type} onChange={e=>setRecurringForm(f=>({...f,type:e.target.value,category:STD_CATS[e.target.value][0].l}))} style={{padding:"12px",borderRadius:13,border:"2px solid #E8EDFF",background:"white",fontSize:14,fontFamily:"inherit",fontWeight:800,color:"#1E1B4B"}}>
                  <option value="expense">Expense</option>
                  <option value="income">Income</option>
                </select>
                <select value={recurringForm.category} onChange={e=>setRecurringForm(f=>({...f,category:e.target.value}))} style={{padding:"12px",borderRadius:13,border:"2px solid #E8EDFF",background:"white",fontSize:14,fontFamily:"inherit",fontWeight:800,color:"#1E1B4B"}}>
                  {STD_CATS[recurringForm.type || "expense"].map(c=><option key={c.l} value={c.l}>{c.icon} {c.l}</option>)}
                </select>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9,marginBottom:9}}>
                <input type="text" inputMode="decimal" placeholder="Amount" value={recurringForm.amount} onKeyDown={blockInvalidNumberKeys} onChange={e=>setRecurringForm(f=>({...f,amount:cleanMoneyInput(e.target.value)}))} style={{padding:"12px",borderRadius:13,border:"2px solid #E8EDFF",background:"white",fontSize:14,fontFamily:"inherit",fontWeight:800,color:"#1E1B4B",outline:"none"}}/>
                <select value={recurringForm.frequency} onChange={e=>setRecurringForm(f=>({...f,frequency:e.target.value}))} style={{padding:"12px",borderRadius:13,border:"2px solid #E8EDFF",background:"white",fontSize:14,fontFamily:"inherit",fontWeight:800,color:"#1E1B4B"}}>
                  {RECURRING_FREQUENCIES.map(f=><option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9,marginBottom:9}}>
                <input type="date" value={recurringForm.nextDate} onChange={e=>setRecurringForm(f=>({...f,nextDate:e.target.value}))} style={{padding:"12px",borderRadius:13,border:"2px solid #E8EDFF",background:"white",fontSize:14,fontFamily:"inherit",fontWeight:800,color:"#1E1B4B",outline:"none"}}/>
                <select value={recurringForm.paymentMethod} onChange={e=>setRecurringForm(f=>({...f,paymentMethod:e.target.value}))} style={{padding:"12px",borderRadius:13,border:"2px solid #E8EDFF",background:"white",fontSize:14,fontFamily:"inherit",fontWeight:800,color:"#1E1B4B"}}>
                  <option value="">Payment mode</option>
                  {PAYMENT_METHODS.map(m=><option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <input placeholder="Note optional" value={recurringForm.note} onChange={e=>setRecurringForm(f=>({...f,note:sanitizeNoteInput(e.target.value, FIELD_LIMITS.note)}))} style={{width:"100%",boxSizing:"border-box",padding:"12px 14px",borderRadius:13,border:"2px solid #E8EDFF",background:"white",fontSize:14,fontFamily:"inherit",fontWeight:800,color:"#1E1B4B",outline:"none",marginBottom:10}}/>
              <button onClick={saveRecurringRule} style={{width:"100%",border:"none",borderRadius:15,padding:"14px",background:ac,color:"white",fontFamily:"inherit",fontWeight:900,fontSize:14,cursor:"pointer"}}>Save Recurring Rule</button>
            </div>
            <div style={{fontSize:13,fontWeight:900,color:tm.text,marginBottom:10}}>Saved rules</div>
            {recurringRules.length === 0 ? <div style={{background:"white",border:"1.5px dashed #CBD5E1",borderRadius:16,padding:16,fontSize:12,fontWeight:800,color:"#64748B",lineHeight:1.55}}>No recurring rules yet. Add common items like rent, salary, SIP, EMI, or subscription.</div> : recurringRules.map(rule=>(
              <div key={rule.id} style={{background:"white",border:"1.5px solid #E8EDFF",borderRadius:16,padding:13,marginBottom:9}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:10}}>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:900,color:"#1E1B4B"}}>{rule.type==="income"?"↑":"↓"} {rule.title}</div>
                    <div style={{fontSize:11,fontWeight:800,color:"#94A3B8",marginTop:3}}>{rule.category} · {rule.frequency} · next {fmtDate(rule.nextDate)}</div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontSize:14,fontWeight:900,color:rule.type==="income"?"#16A34A":"#DC2626"}}>{fmtFull(rule.amount)}</div>
                    <button onClick={()=>deleteRecurringRule(rule.id)} style={{border:"none",background:"#FEE2E2",color:"#DC2626",borderRadius:9,padding:"6px 9px",fontSize:11,fontWeight:900,cursor:"pointer",marginTop:6}}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </>}
        </Sheet>
      )}


      {/* PRODUCTIVITY: ONBOARDING */}
      {showOnboarding && (
        <Sheet onClose={finishOnboarding} showClose={false} title="Getting Started">
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:14,marginBottom:18}}>
            <div>
              <div style={{fontSize:20,fontWeight:900,color:tm.text,marginBottom:4}}>🚀 Get more from FinVault</div>
              <div style={{fontSize:12,color:tm.textSub}}>Quick setup checklist for daily use.</div>
            </div>
            <button onClick={finishOnboarding} aria-label="Close getting started" style={{width:42,height:42,borderRadius:14,border:"none",background:tm.cardAlt||"#F1F5F9",color:tm.text,fontSize:24,lineHeight:1,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>×</button>
          </div>
          {[
            ["1", "Create profiles", "Use Standard for income/expense, Lending for people, and Trading for portfolio records."],
            ["2", "Set budget & recurring", "Add monthly/category budgets and recurring entries for salary, rent, EMI, SIP, and subscriptions."],
            ["3", "Backup regularly", "Export JSON backup before changing phones. Local encrypted data cannot be recovered without PIN."],
            ["4", "Export-only safety", "File import and image upload are disabled for now to reduce injection and storage risks."],
          ].map(item=>(
            <div key={item[0]} style={{display:"flex",gap:12,background:"white",border:"1.5px solid #E8EDFF",borderRadius:16,padding:14,marginBottom:10}}>
              <div style={{width:34,height:34,borderRadius:12,background:ac+"18",color:ac,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:900,flexShrink:0}}>{item[0]}</div>
              <div><div style={{fontSize:14,fontWeight:900,color:"#1E1B4B"}}>{item[1]}</div><div style={{fontSize:12,fontWeight:750,color:"#64748B",lineHeight:1.5,marginTop:3}}>{item[2]}</div></div>
            </div>
          ))}
          <button onClick={finishOnboarding} style={{width:"100%",padding:"15px",border:"none",borderRadius:16,cursor:"pointer",fontFamily:"inherit",fontWeight:900,fontSize:15,background:ac,color:"white",marginTop:6}}>Got it</button>
        </Sheet>
      )}

      {/* BUDGET SETTINGS */}
      {showBudget && (
        <BudgetSheet
          tm={tm}
          color={ac}
          form={budgetForm}
          setForm={setBudgetForm}
          onSave={saveBudgetSettings}
          onClose={()=>setShowBudget(false)}
        />
      )}

      {/* CURRENT VALUE */}
      {showCVS && (
        <Sheet onClose={()=>setShowCVS(false)}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:18,fontWeight:900,color:"#1E1B4B"}}>Update Portfolio Value</div>
            <button onClick={()=>setShowCVS(false)} style={{width:32,height:32,borderRadius:10,border:"none",background:"#F1F5F9",cursor:"pointer",fontSize:16}}>✕</button>
          </div>
          {(active?.cvHistory||[]).length>0 && (
            <div style={{background:"#F8FAFF",borderRadius:14,padding:"12px 14px",marginBottom:16,border:"1.5px solid #E8EDFF"}}>
              <div style={{fontSize:11,fontWeight:800,color:"#94A3B8",marginBottom:3}}>LAST UPDATED</div>
              <div style={{fontSize:14,fontWeight:800,color:"#1E1B4B"}}>{fmtDate(active.cvHistory[0].date)} — {fmtFull(active.cvHistory[0].value)}</div>
            </div>
          )}
          <div style={{background:"#F8FAFF",borderRadius:16,padding:"16px",marginBottom:20,border:"2px solid #E8EDFF"}}>
            <div style={{fontSize:11,fontWeight:800,color:"#94A3B8",letterSpacing:1,textTransform:"uppercase",marginBottom:8}}>Today's Portfolio Value</div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:22,fontWeight:900,color:"#94A3B8"}}>₹</span>
              <input type="text" inputMode="decimal" placeholder="0" value={cvInput} onKeyDown={blockInvalidNumberKeys} onChange={e=>setCVI(cleanMoneyInput(e.target.value))} autoFocus
                style={{flex:1,border:"none",background:"transparent",fontSize:30,fontWeight:900,color:"#1E1B4B",outline:"none",fontFamily:"inherit"}}/>
            </div>
          </div>
          <button onClick={saveCV} style={{width:"100%",padding:"17px",border:"none",borderRadius:16,cursor:"pointer",fontFamily:"inherit",fontWeight:900,fontSize:16,background:ac,color:"white"}}>
            Save Today's Value
          </button>
        </Sheet>
      )}

      {/* EXPORT */}
      {showExport && (
        <Sheet onClose={()=>setShowExport(false)} showClose={false} title="Export and backup">
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:14,marginBottom:18}}>
            <div>
              <div style={{fontSize:18,fontWeight:900,color:tm.text,marginBottom:4}}>📤 Export & Backup</div>
              <div style={{fontSize:12,color:tm.textSub}}>Download or backup your data</div>
            </div>
            <button
              type="button"
              onClick={()=>setShowExport(false)}
              aria-label="Close Export & Backup"
              title="Close"
              style={{width:42,height:42,borderRadius:14,border:"none",background:tm.cardAlt||"#F1F5F9",color:tm.text,fontSize:24,lineHeight:1,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 8px 20px rgba(15,23,42,.06)",flexShrink:0}}
            >×</button>
          </div>

          {/* PDF */}
          <button onClick={secureExportPDF}
            style={{width:"100%",padding:"14px",background:"linear-gradient(135deg,#DC2626,#EF4444)",border:"none",borderRadius:14,cursor:"pointer",color:"white",fontFamily:"inherit",fontWeight:800,fontSize:14,marginBottom:10,display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:22}}>📄</span>
            <div style={{textAlign:"left"}}>
              <div>Export as PDF Report</div>
              <div style={{fontSize:11,opacity:0.8}}>Summary with all profiles</div>
            </div>
          </button>

          {/* Excel */}
          <button onClick={secureExportExcel}
            style={{width:"100%",padding:"14px",background:"linear-gradient(135deg,#059669,#10B981)",border:"none",borderRadius:14,cursor:"pointer",color:"white",fontFamily:"inherit",fontWeight:800,fontSize:14,marginBottom:10,display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:22}}>📊</span>
            <div style={{textAlign:"left"}}>
              <div>Export as CSV (Excel compatible)</div>
              <div style={{fontSize:11,opacity:0.8}}>All profiles & transactions, export-only safe</div>
            </div>
          </button>

          {/* JSON Backup */}
          <button onClick={secureExportJson}
            style={{width:"100%",padding:"14px",background:"linear-gradient(135deg,#4F46E5,#7C3AED)",border:"none",borderRadius:14,cursor:"pointer",color:"white",fontFamily:"inherit",fontWeight:800,fontSize:14,marginBottom:10,display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:22}}>💾</span>
            <div style={{textAlign:"left"}}>
              <div>Backup as JSON</div>
              <div style={{fontSize:11,opacity:0.8}}>Includes 30-day activity log</div>
            </div>
          </button>

          {/* Encrypted JSON Backup */}
          <div style={{background:tm.cardAlt,border:`1.5px solid ${dark?"#334155":"#E0E7FF"}`,borderRadius:16,padding:14,marginBottom:10}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:10}}>
              <span style={{fontSize:22}}>🔐</span>
              <div>
                <div style={{fontSize:14,fontWeight:900,color:tm.text}}>Encrypted Backup (.fvault)</div>
                <div style={{fontSize:11,color:tm.textSub,lineHeight:1.45}}>Password-protected backup using WebCrypto AES-GCM. Keep the password safely; FinVault cannot recover it.</div>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
              <input type="password" autoComplete="new-password" placeholder="Backup password" value={encryptedExportForm.password} onChange={e=>setEncryptedExportForm(f=>({...f,password:e.target.value.slice(0,120)}))}
                style={{width:"100%",boxSizing:"border-box",padding:"12px",borderRadius:12,border:`1.5px solid ${dark?"#475569":"#CBD5E1"}`,background:dark?"#0F172A":"#FFFFFF",color:tm.text,fontFamily:"inherit",fontWeight:800,outline:"none"}}/>
              <input type="password" autoComplete="new-password" placeholder="Confirm password" value={encryptedExportForm.confirm} onChange={e=>setEncryptedExportForm(f=>({...f,confirm:e.target.value.slice(0,120)}))}
                style={{width:"100%",boxSizing:"border-box",padding:"12px",borderRadius:12,border:`1.5px solid ${dark?"#475569":"#CBD5E1"}`,background:dark?"#0F172A":"#FFFFFF",color:tm.text,fontFamily:"inherit",fontWeight:800,outline:"none"}}/>
            </div>
            <button onClick={secureExportEncryptedJson} disabled={encryptedExportForm.busy}
              style={{width:"100%",padding:"13px",background:encryptedExportForm.busy?"#94A3B8":"linear-gradient(135deg,#111827,#4F46E5)",border:"none",borderRadius:13,cursor:encryptedExportForm.busy?"not-allowed":"pointer",color:"white",fontFamily:"inherit",fontWeight:900,fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              {encryptedExportForm.busy ? "Encrypting..." : "Download Encrypted Backup"}
            </button>
          </div>

          <div style={{background:dark?"#0F172A":"#FFF7ED",border:`1.5px solid ${dark?"#334155":"#FED7AA"}`,borderRadius:14,padding:"11px 12px",fontSize:11,color:dark?"#CBD5E1":"#9A3412",lineHeight:1.45,marginBottom:10}}>
            🔒 Normal Firebase/Firestore data is encrypted by Google at rest and HTTPS in transit. Encrypted backup adds app-level protection for downloaded backup files.
          </div>

          {/* Activity Log */}
          <button onClick={secureExportActivityJson}
            style={{width:"100%",padding:"14px",background:"linear-gradient(135deg,#0F766E,#14B8A6)",border:"none",borderRadius:14,cursor:"pointer",color:"white",fontFamily:"inherit",fontWeight:800,fontSize:14,marginBottom:10,display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:22}}>🧾</span>
            <div style={{textAlign:"left"}}>
              <div>Export Activity Log</div>
              <div style={{fontSize:11,opacity:0.8}}>Create, update, delete actions from last 30 days</div>
            </div>
          </button>


          {/* Currency */}
          <div style={{background:tm.cardAlt,borderRadius:14,padding:14,marginBottom:4}}>
            <div style={{fontSize:12,fontWeight:800,color:tm.textSub,marginBottom:10}}>🌐 Currency</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
              {CURRENCIES.map(c=>{
                const cur=getSettings().currencySymbol||"₹";
                return(
                  <button key={c.code} onClick={()=>{saveSettings({...getSettings(),currencySymbol:c.symbol,currencyCode:c.code});setShowExport(false);window.location.reload();}}
                    style={{padding:"10px 6px",borderRadius:12,border:`2px solid ${cur===c.symbol?"#4F46E5":tm.border}`,background:cur===c.symbol?"#EEF2FF":tm.card,cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s"}}>
                    <div style={{fontSize:17,fontWeight:900,color:cur===c.symbol?"#4F46E5":tm.text}}>{c.symbol}</div>
                    <div style={{fontSize:9,color:tm.textSub,fontWeight:700,marginTop:2}}>{c.code}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </Sheet>
      )}

      {showFreeInfo && (
        <FreePlanSheet
          tm={tm}
          mode={storageMode}
          stats={freePlanStats}
          onClose={()=>setShowFreeInfo(false)}
          onExportBackup={exportJsonBackup}
          onChangeStorage={()=>{ setShowFreeInfo(false); onChangeStorage(); }}
          onCleanupActive={()=>cleanupReceipts("active")}
          onCleanupAll={()=>cleanupReceipts("all")}
        />
      )}

      {showSafetyInfo && (
        <SecuritySafetySheet
          tm={tm}
          mode={storageMode}
          onClose={()=>setShowSafetyInfo(false)}
          onOpenPrivacy={()=>openInternalPage("/privacy")}
        />
      )}

      {/* CONFIRMS */}
      {/* ── REPORTS ── */}
      {tab==="reports" && (
        <ScrollPage>
          <div style={{paddingTop:52}}>
            <div style={{padding:"0 16px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:22,fontWeight:900,color:tm.text}}>Reports</div>
            </div>
            <Reports accounts={accounts} txnMap={txnMap} dark={dark} tm={tm}/>
          </div>
        </ScrollPage>
      )}

      {/* ── JOURNAL ── */}
      {tab==="journal" && (
        <ScrollPage>
          <div style={{paddingTop:52}}>
            <div style={{padding:"0 16px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:22,fontWeight:900,color:tm.text}}>Trade Journal</div>
                <div style={{fontSize:12,color:tm.textSub,marginTop:2}}>Record & review your trades</div>
              </div>
            </div>
            <TradeJournal dark={dark} tm={tm}/>
          </div>
        </ScrollPage>
      )}

      {/* PWA INSTALL BANNER */}
      {showInstall && (
        <div style={{position:"fixed",bottom:90,left:"50%",transform:"translateX(-50%)",width:"calc(100% - 32px)",maxWidth:398,zIndex:150,
          background:"linear-gradient(135deg,#4F46E5,#7C3AED)",borderRadius:20,padding:"14px 16px",
          boxShadow:"0 8px 32px rgba(79,70,229,0.4)",display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:28}}>📱</span>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:900,color:"white"}}>Install FinVault App</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.7)"}}>Add to home screen for best experience</div>
          </div>
          <button onClick={handleInstall}
            style={{background:"white",border:"none",borderRadius:10,padding:"8px 14px",cursor:"pointer",
              fontFamily:"inherit",fontWeight:900,fontSize:12,color:"#4F46E5",flexShrink:0}}>
            Install
          </button>
          <button onClick={()=>setShowInstall(false)}
            style={{background:"rgba(255,255,255,0.15)",border:"none",borderRadius:10,padding:"8px 10px",
              cursor:"pointer",color:"white",fontSize:14,flexShrink:0}}>
            ✕
          </button>
        </div>
      )}



      {showIosInstall && tab === "home" && (
        <div className="ios-install-banner" style={{position:"fixed",left:16,right:16,bottom:"calc(104px + env(safe-area-inset-bottom))",zIndex:140,background:"rgba(255,255,255,0.92)",border:"1px solid rgba(226,232,240,0.95)",borderRadius:18,padding:"12px 12px",boxShadow:"0 18px 42px rgba(15,23,42,0.12)",display:"flex",alignItems:"center",gap:10,maxWidth:398,margin:"0 auto"}}>
          <BrandMark size={36}/>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:12,fontWeight:900,color:"#1E1B4B"}}>Get full app view</div>
            <div style={{fontSize:10.5,fontWeight:800,color:"#64748B",lineHeight:1.35}}>Safari: Share → Add to Home Screen</div>
          </div>
          <button onClick={dismissIosInstall} aria-label="Dismiss install tip" style={{width:34,height:34,minHeight:34,border:"none",borderRadius:12,background:"#F1F5F9",color:"#64748B",fontSize:18,fontWeight:900,cursor:"pointer"}}>×</button>
        </div>
      )}

      {/* FORCE PIN SETUP for local mode */}
      {showForcePinSetup && !isPinEnabled() && (
        <div style={{position:"fixed",inset:0,zIndex:400,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",padding:"0 24px"}}>
          <div style={{background:"white",borderRadius:24,padding:"28px 24px",textAlign:"center",width:"100%",maxWidth:340}}>
            <div style={{fontSize:40,marginBottom:12}}>🔐</div>
            <div style={{fontSize:17,fontWeight:900,color:"#1E1B4B",marginBottom:8}}>Secure Your Data</div>
            <div style={{fontSize:13,color:"#94A3B8",marginBottom:14,lineHeight:1.6}}>
              Your financial data is stored on this device. Set a PIN to protect it from unauthorized access and encrypt the local vault.
            </div>
            <div style={{background:"#FFF7ED",border:"1.5px solid #FED7AA",borderRadius:14,padding:"10px 12px",marginBottom:18,textAlign:"left"}}>
              <div style={{fontSize:12,fontWeight:900,color:"#C2410C",marginBottom:4}}>Important recovery note</div>
              <div style={{fontSize:11.2,fontWeight:800,color:"#64748B",lineHeight:1.5}}>
                If you forget this PIN in Local Mode, FinVault cannot recover encrypted local data by email. Export a JSON backup regularly.
              </div>
            </div>
            <button onClick={()=>{setShowForcePinSetup(false);setShowPinSetup(true);}}
              style={{width:"100%",padding:"14px",background:"linear-gradient(135deg,#4F46E5,#7C3AED)",border:"none",borderRadius:14,cursor:"pointer",fontFamily:"inherit",fontWeight:900,fontSize:15,color:"white",marginBottom:10}}>
              🔐 Set PIN Now
            </button>
            <button onClick={()=>setShowForcePinSetup(false)}
              style={{width:"100%",padding:"12px",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",fontWeight:700,fontSize:13,color:"#94A3B8"}}>
              Skip for now (unencrypted)
            </button>
          </div>
        </div>
      )}

      {/* PIN SETUP */}
      {showPinSetup && (
        <PinLock mode="setup"
          onSuccess={()=>setShowPinSetup(false)}
          onCancel={()=>setShowPinSetup(false)}/>
      )}

      {(delId||delAccId) && (
        <ConfirmDlg
          title={delAccId?"Delete Profile?":"Delete Entry?"}
          msg={delAccId?"All transactions for this profile will be permanently deleted from the cloud.":"This cannot be undone."}
          onCancel={()=>{setDelId(null);setDelAccId(null);}}
          onConfirm={()=>delId?handleDelTx(delId):handleDeleteAcc(delAccId)}/>
      )}
    </Wrap>
  );
}
